const axios = require('axios');
import { getAccessToken, rememberRoleFromProfile } from '../utils/auth';
import { getCachedRates, getAnyCachedRates, cacheRates,
         getCachedCountries, getAnyCachedCountries, cacheCountries } from '../utils/currency';

/**
 * Merchant KYC endpoints.
 * Centralized here so the paths can be corrected in ONE place if the backend differs.
 */
/**
 * Logged-in MERCHANT KYC.
 *   GET  /v1/merchant/kyc  -> check KYC status
 *   POST /v1/merchant/kyc  -> submit KYC (multipart/form-data)
 * A rejected KYC can be resubmitted with POST - only the corrected fields need to be sent.
 */
const MERCHANT_KYC_ENDPOINT = 'merchant/kyc';

/**
 * Payments / wallet top-up.
 *   POST payment/initiate -> { gateway, amount, purpose, remarks }
 *        => { order_id, amount, status, provider_reference, client_secret, publishable_key }
 *
 * The card details never touch our server: the client_secret is handed to Stripe.js and
 * the card is collected by Stripe's own iframe (the old flow POSTed the raw PAN to
 * `stripeload`, which is a PCI problem).
 */
const PAYMENT_GATEWAYS = {
  initiate: 'payment/initiate',
  defaultPurpose: 'wallet_load',
  stripeJs: 'https://js.stripe.com/v3/'
};

/**
 * User MPIN (6-digit transaction PIN).
 *   GET  /v1/mpin         -> { has_mpin }
 *   POST /v1/mpin/set     -> { password, mpin, confirm_mpin }   (first-time or reset)
 *   POST /v1/mpin/change  -> { old_mpin, mpin, confirm_mpin }
 *   POST /v1/mpin/verify  -> { mpin }  =>  200 { valid: true|false }
 */
const USER_MPIN = {
  status: 'mpin',
  set: 'mpin/set',
  change: 'mpin/change',
  verify: 'mpin/verify'
};

/**
 * USER KYC (individual account) - distinct from the merchant KYC above.
 *   GET  /v1/kyc  -> status, limits, withdraw_limits, can_edit, can_request_update
 *   POST /v1/kyc  -> multipart (selfie, front_image, back_image + personal/address fields)
 * Document types come from the shared kyc/document-types endpoint.
 */
const USER_KYC = {
  get: 'kyc',
  create: 'kyc',
  updateRequest: 'kyc/update-requests'   // POST - full KYC payload, admin approves
};

const KYC_ENDPOINTS = {
  get: MERCHANT_KYC_ENDPOINT,        // GET  - fetch/check current merchant KYC
  create: MERCHANT_KYC_ENDPOINT,     // POST - submit merchant KYC (multipart)
  updateRequest: 'merchant/kyc/update-requests' // POST - full KYC payload, admin approves
};

/**
 * KYC identity-document endpoints (confirmed against the API docs).
 *   GET    /v1/kyc/document-types  -> [{id, name, code, requires_back, order}]
 *   GET    /v1/kyc/documents       -> uploaded documents
 *   POST   /v1/kyc/documents       -> multipart: document_type*, document_number, front_image*, back_image
 *   DELETE /v1/kyc/documents/{id}  -> remove a document before KYC is verified
 */
/** Accepts [], {data:[]}, {results:[]} or {documents:[]} and always returns an array. */
function normalizeList(payload){
  if (Array.isArray(payload)) { return payload; }
  if (!payload || typeof payload !== 'object') { return []; }
  if (Array.isArray(payload.data)) { return payload.data; }
  if (Array.isArray(payload.results)) { return payload.results; }
  if (Array.isArray(payload.documents)) { return payload.documents; }
  return [];
}

/**
 * True when a response body is a web-server PAGE rather than an API answer.
 *
 * A missing DRF route with DEBUG on returns a full HTML stack trace. Passed to
 * extractError it becomes the first 3 kB of markup, which is what ended up printed
 * inside the dialog.
 */
function isHtmlErrorBody(error){
  const res = error && error.response;
  if (!res) { return false; }
  const type = String((res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || '');
  const body = typeof res.data === 'string' ? res.data : '';
  return type.indexOf('text/html') !== -1 || /^\s*<(!doctype|html)/i.test(body);
}

/** True when the route itself is absent, as opposed to the request being rejected. */
function isMissingEndpoint(error){
  const status = error && error.response && error.response.status;
  if ([404, 405, 501].indexOf(status) === -1) { return false; }
  // A JSON 404 is the API talking ("not found"); an HTML one is a missing route.
  return isHtmlErrorBody(error) || !(error.response.data && typeof error.response.data === 'object');
}

function extractError(error, fallback){
  const d = error.response && error.response.data;
  if (!d) { return fallback; }
  // Never surface an HTML error page as if it were a message.
  if (isHtmlErrorBody(error)) { return fallback; }
  if (typeof d === 'string') { return d.length > 300 ? fallback : d; }
  if (d.message) { return d.message; }
  if (d.detail) { return d.detail; }
  const firstKey = Object.keys(d)[0];
  if (firstKey) {
    const v = d[firstKey];
    return Array.isArray(v) ? (firstKey + ': ' + v[0]) : (firstKey + ': ' + v);
  }
  return fallback;
}

/**
 * Step 1 of becoming a merchant: a prospective merchant submits an application.
 * PUBLIC (no account yet), application/json - NOT multipart, NO auth header.
 */
/**
 * Signed-in user's own account.
 *   DELETE account/delete  body: { password }  -> permanently removes the account
 */
const ACCOUNT_ENDPOINTS = {
  remove: 'account/delete'
};

const MERCHANT_APPLY_ENDPOINT = 'merchant/apply';

/**
 * Merchant payments (transaction list / detail / export).
 *   GET merchant/payments            -> { count, limit, offset, results: [...] }
 *   GET merchant/payments/{pidx}     -> single payment
 *   GET merchant/payments/export     -> file download (CSV/XLSX)
 *
 * PARAM NAMES ARE CENTRALISED HERE - if the backend uses different query keys,
 * change them once in this object and every screen follows.
 */
const MERCHANT_PAYMENTS = {
  list: 'merchant/payments',
  detail: 'merchant/payments/',      // + pidx
  export: 'merchant/payments/export',
  params: {
    status: 'status',
    search: 'search',
    dateFrom: 'date_from',
    dateTo: 'date_to',
    environment: 'environment',
    limit: 'limit',
    offset: 'offset'
  }
};

/**
 * USER wallet STATEMENT (ledger).
 *   GET statement           list + wallet + summary + pagination
 *   GET statement/{id}      one entry, with fields the list does not carry
 *   GET statement/export    server-generated file, ?format=pdf|csv
 *
 * Same self-describing `filters.available` block as the merchant statement, plus an
 * amount range and a transaction-code search.
 */
const USER_STATEMENT = {
  list: 'statement',
  detail: 'statement/',     // + id
  export: 'statement/export',
  params: {
    code: 'code',
    direction: 'direction',
    type: 'type',
    status: 'status',
    period: 'period',
    start: 'start',
    end: 'end',
    search: 'search',
    minAmount: 'min_amount',
    maxAmount: 'max_amount',
    format: 'format',
    limit: 'limit',
    offset: 'offset'
  }
};

/** Only sends what is actually set - an empty param is not the same as no filter. */
function buildUserStatementQuery(filters){
  const f = filters || {};
  const k = USER_STATEMENT.params;
  const q = {};
  if (f.code) { q[k.code] = f.code; }
  if (f.direction) { q[k.direction] = f.direction; }
  if (f.type) { q[k.type] = f.type; }
  if (f.status) { q[k.status] = f.status; }
  // `all` is the API's own default, and start/end override period anyway.
  if (f.period && f.period !== 'all') { q[k.period] = f.period; }
  if (f.start) { q[k.start] = f.start; }
  if (f.end) { q[k.end] = f.end; }
  if (f.search) { q[k.search] = f.search; }
  // 0 is a legitimate bound, so check for presence rather than truthiness.
  if (f.minAmount !== undefined && f.minAmount !== null && f.minAmount !== '') {
    q[k.minAmount] = f.minAmount;
  }
  if (f.maxAmount !== undefined && f.maxAmount !== null && f.maxAmount !== '') {
    q[k.maxAmount] = f.maxAmount;
  }
  if (f.format) { q[k.format] = f.format; }
  // The API caps page size at 100.
  if (f.limit !== undefined && f.limit !== null) { q[k.limit] = Math.min(Number(f.limit) || 20, 100); }
  if (f.offset !== undefined && f.offset !== null) { q[k.offset] = f.offset; }
  return q;
}

/**
 * Merchant wallet STATEMENT (ledger).
 *   GET merchant/statement
 *
 * Returns wallet, range, filters{applied,available}, summary, pagination and results.
 * The `filters.available` block is self-describing, so the screen builds its dropdowns
 * from the response rather than hard-coding option lists that would drift.
 */
const MERCHANT_STATEMENT = {
  list: 'merchant/statement',
  params: {
    period: 'period',
    direction: 'direction',
    type: 'type',
    status: 'status',
    start: 'start',
    end: 'end',
    search: 'search',
    environment: 'environment',
    limit: 'limit',
    offset: 'offset'
  }
};

function buildStatementQuery(filters){
  const f = filters || {};
  const k = MERCHANT_STATEMENT.params;
  const q = {};
  // "all" is the API's own default - sending it back adds nothing.
  if (f.period && f.period !== 'all') { q[k.period] = f.period; }
  if (f.direction) { q[k.direction] = f.direction; }
  if (f.type) { q[k.type] = f.type; }
  if (f.status) { q[k.status] = f.status; }
  if (f.start) { q[k.start] = f.start; }
  if (f.end) { q[k.end] = f.end; }
  if (f.search) { q[k.search] = f.search; }
  if (f.environment) { q[k.environment] = f.environment; }
  if (f.limit !== undefined && f.limit !== null) { q[k.limit] = f.limit; }
  if (f.offset !== undefined && f.offset !== null) { q[k.offset] = f.offset; }
  return q;
}

/** Shape returned to components, so a missing block never crashes a template. */
function normalizeStatement(d, filters){
  const data = d || {};
  const pag = data.pagination || {};
  return {
    wallet: data.wallet || {},
    range: data.range || {},
    filters: data.filters || {},
    summary: data.summary || {},
    pagination: {
      count: pag.count || 0,
      limit: pag.limit || (filters && filters.limit) || 20,
      offset: pag.offset || 0
    },
    results: Array.isArray(data.results) ? data.results : []
  };
}

/**
 * Merchant dashboard / analytics / customers.
 * All three accept `environment` so they follow the Live-Sandbox switch.
 */
const MERCHANT_INSIGHTS = {
  dashboard: 'merchant/dashboard',
  analytics: 'merchant/analytics',
  customers: 'merchant/customers',
  params: { environment: 'environment', days: 'days' }
};

function buildInsightQuery(filters){
  const f = filters || {};
  const k = MERCHANT_INSIGHTS.params;
  const q = {};
  if (f.environment) { q[k.environment] = f.environment; }
  if (f.days) { q[k.days] = f.days; }
  return q;
}

/**
 * Merchant withdrawals.
 *   GET  merchant/withdraws                 -> list + withdraw_limits + available filters
 *   POST merchant/withdraws                 -> { amount, remarks }
 *   POST merchant/withdraws/{id}/cancel     -> merchant cancels their own request
 *   POST merchant/withdraws/{id}/review     -> ADMIN approve/decline (kept for the admin screen)
 *   GET  merchant/withdraws/analytics       -> totals, status breakdown, daily series
 */
const MERCHANT_WITHDRAWS = {
  list: 'merchant/withdraws',
  create: 'merchant/withdraws',
  quote: 'merchant/withdraws/quote',
  analytics: 'merchant/withdraws/analytics',
  cancel: function (id) { return 'merchant/withdraws/' + id + '/cancel'; },
  review: function (id) { return 'merchant/withdraws/' + id + '/review'; },
  params: {
    status: 'status', period: 'period', search: 'search',
    start: 'start', end: 'end',
    minAmount: 'min_amount', maxAmount: 'max_amount',
    limit: 'limit', offset: 'offset'
  },
  /** The endpoint rejects anything else; `custom` is ours, not the API's. */
  periods: ['today', '7d', '30d', '90d', 'month', 'year', 'all'],
  maxLimit: 100
};

/**
 * Query string for GET merchant/withdraws.
 *
 * Two things were wrong before and silently produced an unfiltered list:
 *   - the date range went out as date_from / date_to. The endpoint reads `start` / `end`,
 *     so it ignored both and returned everything - the "date filter does not work" bug.
 *   - `period` and a date range were sent together. The API takes `period` OR `start`/`end`,
 *     and a period would win over the range even once the names were right.
 * A custom range therefore suppresses `period` entirely, and vice versa.
 */
function buildWithdrawQuery(filters){
  const f = filters || {};
  const k = MERCHANT_WITHDRAWS.params;
  const q = {};
  if (f.status) { q[k.status] = f.status; }
  if (f.search) { q[k.search] = f.search; }

  const hasRange = !!(f.start || f.end);
  if (hasRange) {
    if (f.start) { q[k.start] = f.start; }
    if (f.end) { q[k.end] = f.end; }
  } else if (f.period && MERCHANT_WITHDRAWS.periods.indexOf(f.period) !== -1) {
    q[k.period] = f.period;
  }

  // Amount bounds are CENTS, like every other money value this API takes.
  if (f.minAmount !== undefined && f.minAmount !== null && f.minAmount !== '') {
    q[k.minAmount] = f.minAmount;
  }
  if (f.maxAmount !== undefined && f.maxAmount !== null && f.maxAmount !== '') {
    q[k.maxAmount] = f.maxAmount;
  }

  if (f.limit !== undefined && f.limit !== null) {
    q[k.limit] = Math.min(Number(f.limit) || 20, MERCHANT_WITHDRAWS.maxLimit);
  }
  if (f.offset !== undefined && f.offset !== null) { q[k.offset] = f.offset; }
  return q;
}

/**
 * User payments (send money).
 *   GET  recipient?email=...  -> { id, full_name, email, image, is_self }
 *   GET  pay                  -> payment list
 *   POST pay                  -> { amount, to, remarks }  =>  { message, transaction, amount }
 *   PUT  pay/confirm          -> { transaction_id, otp, password }  =>  { message }
 *   POST payment-issue        -> report a mistaken payment  (path not confirmed yet)
 */
const USER_PAY = {
  list: 'pay',
  create: 'pay',
  confirm: 'pay/confirm',
  recipient: 'recipient',
  issue: 'payment-issue'
};

/**
 * Payment requests (ask another Amejpay user for money).
 *   GET  requests          -> list (each row carries direction: incoming | outgoing)
 *   POST requests          -> { amount, to, remarks, date }
 *   POST requests/respond  -> { transaction_id, date, status: accepted|rejected } => { message, requestid }
 *   PUT  requests/confirm  -> { transaction_id, otp, password }
 */
const USER_REQUESTS = {
  list: 'requests',
  create: 'requests',
  respond: 'requests/respond',
  confirm: 'requests/confirm'
};

/**
 * User bank accounts + user withdrawals.
 *   GET    adminbanks        -> banks the user may add
 *   GET    banks             -> the user's saved accounts
 *   POST   banks             -> { bank, account_number, bank_type, account_holder_name, branch_name, branch_code }
 *   GET    banks/{id}        -> one saved account
 *   DELETE banks/{id}        -> remove (204)
 *   NOTE: there is no update endpoint - editing is done by create-then-delete.
 *
 *   GET    withdraws         -> withdrawal list
 *   POST   withdraws         -> { bank, amount, remarks } => { message, transaction, amount }
 *   PUT    withdraws/confirm -> { transaction_id, otp, password }
 */
const USER_BANKS = {
  admin: 'adminbanks',
  list: 'banks',
  create: 'banks',
  detail: function (id) { return 'banks/' + id; },
  remove: function (id) { return 'banks/' + id; },
  maxAccounts: 5
};
const USER_WITHDRAWS = {
  list: 'withdraws',
  create: 'withdraws',
  confirm: 'withdraws/confirm'
};

/** Drops empty values so we never send `?status=&search=` */
function buildPaymentQuery(filters){
  const f = filters || {};
  const k = MERCHANT_PAYMENTS.params;
  const q = {};
  if (f.status) { q[k.status] = f.status; }
  if (f.search) { q[k.search] = f.search; }
  if (f.dateFrom) { q[k.dateFrom] = f.dateFrom; }
  if (f.dateTo) { q[k.dateTo] = f.dateTo; }
  if (f.environment) { q[k.environment] = f.environment; }
  if (f.limit !== undefined && f.limit !== null) { q[k.limit] = f.limit; }
  if (f.offset !== undefined && f.offset !== null) { q[k.offset] = f.offset; }
  return q;
}

/**
 * User registration (3 steps):
 *   POST auth/register    -> validates + emails an OTP (no account yet)
 *   POST auth/verify-otp  -> creates the account, returns user + tokens
 *   POST auth/resend-otp  -> resends the OTP (2 minute cooldown)
 */
const AUTH_ENDPOINTS = {
  register: 'auth/register',
  verifyOtp: 'auth/verify-otp',
  resendOtp: 'auth/resend-otp'
};

/**
 * Turns a DRF / API error body into { fieldErrors, message }.
 * Handles {field:[msg]}, {message}, {detail}, plain strings and status codes.
 */
/**
 * Dedicated client for the PUBLIC auth endpoints (register / verify-otp / resend-otp).
 *
 * It must (a) send NO Authorization header and (b) DO send cookies, because the
 * registration is held in the Django session between register and verify-otp.
 *
 * WHY THIS EXISTS
 * main.js installs a global axios request interceptor that attaches
 *     Authorization: Bearer <token>
 * to EVERY request whenever any token is present in localStorage/sessionStorage,
 * and ~70 actions also write axios.defaults.headers.common['Authorization'].
 * These auth endpoints are public and must be called with NO credentials - a stale
 * or leftover token changes how the server resolves the request and made
 * verify-otp fail from the browser while the identical call succeeded in Postman.
 *
 * axios.create() gives us an instance that does NOT inherit the global interceptors,
 * and the interceptor below strips any Authorization header that leaked in via defaults.
 */
function createPublicClient(extra){
  const client = axios.create(Object.assign({
    baseURL: process.env.VUE_APP_SERVICE_URL,
    headers: { 'Content-Type': 'application/json' }
  }, extra || {}));
  client.interceptors.request.use(function (config) {
    if (config.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
      if (config.headers.common) {
        delete config.headers.common.Authorization;
        delete config.headers.common.authorization;
      }
    }
    return config;
  });
  return client;
}

/**
 * Public endpoints that need NO account and NO session cookie.
 * POST merchant/apply is one of these - a prospective merchant has no login yet.
 */
const publicClient = createPublicClient();

/**
 * AUTH TRANSPORT - why registration used to break at the OTP step.
 *
 * auth/register does NOT create the account. It parks the details server-side and
 * emails a code; auth/verify-otp is what actually creates the user. The backend ties
 * those two calls together with the Django session cookie.
 *
 * A browser will not send that cookie on a cross-origin request unless
 * withCredentials is on AND the server answers with an exact
 * `Access-Control-Allow-Origin` plus `Access-Control-Allow-Credentials: true`.
 * This API answers with `Access-Control-Allow-Origin: *`, which forbids credentials -
 * so with an absolute VUE_APP_SERVICE_URL the cookie is dropped, verify-otp finds no
 * pending registration, and returns "No user registered". Postman has none of these
 * rules and keeps the cookie in its jar, which is why the same call works there.
 *
 * The fix is to make the auth pair SAME-ORIGIN, so the cookie is not a cross-origin
 * cookie at all. In development the dev server already proxies AUTH_PROXY_PREFIX to
 * the real API host (see vue.config.js), so we simply route auth through it whenever
 * VUE_APP_SERVICE_URL is absolute. No .env edit is required.
 *
 * Resolution order for the auth base URL:
 *   1. VUE_APP_AUTH_BASE_URL          - explicit override, always wins
 *   2. dev + absolute service URL     - AUTH_PROXY_PREFIX (same-origin, cookies flow)
 *   3. VUE_APP_SERVICE_URL            - as-is
 *
 * In PRODUCTION there is no dev server, so the API must either be served from the same
 * origin as the app, or return exact-origin + Access-Control-Allow-Credentials: true
 * (then set VUE_APP_AUTH_WITH_CREDENTIALS=true). See USER_REGISTER_SESSION_FIX.md.
 */
const SERVICE_URL = process.env.VUE_APP_SERVICE_URL || '';
const IS_ABSOLUTE_SERVICE_URL = /^https?:\/\//i.test(SERVICE_URL);
const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

/* =====================================================================
 * AUTH TRANSPORT
 *
 * auth/register does not create the account. It parks the details server-side and
 * emails a code; auth/verify-otp is what creates the user. The backend ties those two
 * calls together with the Django SESSION COOKIE, so the cookie has to survive between
 * them.
 *
 * A browser only sends a cookie cross-origin when the request is credentialed AND the
 * server replies with an exact Access-Control-Allow-Origin plus
 * Access-Control-Allow-Credentials: true.
 *
 * NOTE ON THE HOST CHANGE: while the API lived at api.amejpays.com it shared a
 * registrable domain with the site, so the pair was same-SITE and a SameSite=Lax
 * cookie could flow once CORS allowed credentials. With the API now on
 * api.amejpay.com, that is only still true if the SITE is also on amejpay.com.
 * If the site remains amejpays.com the two are cross-SITE, and a Lax cookie will
 * not be sent at all - the session then needs SameSite=None; Secure, or the
 * same-origin proxy below.
 *
 * The earlier version ASSUMED the API could not do credentialed CORS and so never even
 * tried in a production build. That assumption is exactly why localhost worked and the
 * live site did not. It now DISCOVERS what works: each transport is tried once, in
 * order, and the winner is remembered for the tab.
 *
 *   1. proxy      same-origin path (AUTH_PROXY_PREFIX). The dev server proxies it;
 *                 in production it works too if nginx reverse-proxies it.
 *   2. direct+    the API host with credentials ON. Works as soon as the backend
 *                 returns proper credentialed CORS headers.
 *   3. direct     the API host with credentials OFF. Register succeeds but the session
 *                 cannot survive, so verify-otp reports the registration as missing.
 *                 Last resort, and it says so loudly in the console.
 *
 * Falling through is free and cannot double-send OTP emails: a JSON POST is
 * preflighted, so if credentials are refused the real request is never sent; and a
 * missing proxy path never reaches the API at all.
 * ===================================================================== */

/** Same-origin path a proxy can map to the API. Not "/v1" - old configs map that to a dead IP. */
const AUTH_PROXY_PREFIX = process.env.VUE_APP_AUTH_PROXY_PREFIX || '/auth-api/';

/** Explicit override always wins, in dev and in production. */
const AUTH_BASE_OVERRIDE = process.env.VUE_APP_AUTH_BASE_URL || '';

/** 'true' forces credentials on, 'false' forces them off, anything else = auto-detect. */
const CREDENTIALS_SETTING = String(process.env.VUE_APP_AUTH_WITH_CREDENTIALS || '').toLowerCase();

/** Set to 'true' to skip the same-origin proxy attempt entirely. */
const AUTH_PROXY_DISABLED = String(process.env.VUE_APP_AUTH_DISABLE_PROXY || '').toLowerCase() === 'true';

const AUTH_DIRECT_BASE = AUTH_BASE_OVERRIDE || SERVICE_URL;
const AUTH_DIRECT_SAME_ORIGIN = !/^https?:\/\//i.test(AUTH_DIRECT_BASE);

function stripAuthHeaders(config){
  if (config.headers) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
    if (config.headers.common) {
      delete config.headers.common.Authorization;
      delete config.headers.common.authorization;
    }
  }
  return config;
}

function makeAuthClient(baseURL, withCredentials){
  const client = axios.create({
    baseURL: baseURL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: withCredentials
  });
  client.interceptors.request.use(stripAuthHeaders);
  return client;
}

/** The ordered list of transports to try. */
function buildAuthTransports(){
  // A same-origin API needs no proxy and no CORS at all.
  if (AUTH_DIRECT_SAME_ORIGIN) {
    return [{ id: 'same-origin', base: AUTH_DIRECT_BASE, credentials: true }];
  }
  // An explicit override means someone has already decided - do not second-guess it.
  if (AUTH_BASE_OVERRIDE) {
    return [{ id: 'override', base: AUTH_DIRECT_BASE, credentials: CREDENTIALS_SETTING !== 'false' }];
  }
  const list = [];
  if (!AUTH_PROXY_DISABLED) {
    list.push({ id: 'proxy', base: AUTH_PROXY_PREFIX, credentials: true });
  }
  if (CREDENTIALS_SETTING !== 'false') {
    list.push({ id: 'direct-credentialed', base: AUTH_DIRECT_BASE, credentials: true });
  }
  if (CREDENTIALS_SETTING !== 'true') {
    list.push({ id: 'direct', base: AUTH_DIRECT_BASE, credentials: false });
  }
  return list;
}

const AUTH_TRANSPORTS = buildAuthTransports();
AUTH_TRANSPORTS.forEach(function (t) { t.client = makeAuthClient(t.base, t.credentials); });

/** Remembered for the tab so the fall-through happens at most once. */
const AUTH_TRANSPORT_KEY = 'amejpay.authTransport';

function rememberTransport(id){
  try { window.sessionStorage.setItem(AUTH_TRANSPORT_KEY, id); } catch (e) { /* private mode */ }
}

let authTransportIndex = (function () {
  let remembered = null;
  try { remembered = window.sessionStorage.getItem(AUTH_TRANSPORT_KEY); } catch (e) { remembered = null; }
  if (!remembered) { return 0; }
  for (let i = 0; i < AUTH_TRANSPORTS.length; i++) {
    if (AUTH_TRANSPORTS[i].id === remembered) { return i; }
  }
  return 0;
})();

/** The base actually in use, for logs and diagnostics. */
let AUTH_BASE_URL = (AUTH_TRANSPORTS[authTransportIndex] || {}).base || AUTH_DIRECT_BASE;

/** True once we are on the last resort - no cookies, so the session cannot survive. */
function onCookielessTransport(){
  const t = AUTH_TRANSPORTS[authTransportIndex];
  return Boolean(t && !t.credentials);
}

/** Is this body a real API answer (JSON object) rather than a web-server page? */
function isApiBody(data){
  if (data && typeof data === 'object') { return true; }
  if (typeof data === 'string') {
    const text = data.trim();
    if (!text) { return false; }
    if (text.charAt(0) === '<') { return false; }
    try { JSON.parse(text); return true; } catch (e) { return false; }
  }
  return false;
}

/**
 * Did the request fail to REACH the API? Those are the only failures worth retrying
 * on another transport.
 *
 *   - no response at all           -> CORS block, DNS, refused connection
 *   - an HTML body                 -> a static host answered with index.html. This can
 *                                     arrive as 200, so the BODY is what decides, not
 *                                     the status code
 *   - 404/405/501/502/503/504 with a non-JSON body -> the path is not proxied here
 *
 * A 404 carrying a JSON body is the API itself talking (for example "Registration data
 * not found") and must NOT trigger a retry.
 */
function isTransportFailure(error){
  const res = error && error.response;
  if (!res) { return true; }
  if (isApiBody(res.data)) { return false; }
  if (responseIsHtml(res)) { return true; }
  return [404, 405, 501, 502, 503, 504].indexOf(res.status) !== -1;
}

function responseIsHtml(response){
  const headers = (response && response.headers) || {};
  const type = String(headers['content-type'] || headers['Content-Type'] || '');
  const body = typeof response.data === 'string' ? response.data : '';
  return type.indexOf('text/html') !== -1 || /^\s*<(!doctype|html)/i.test(body);
}

if (typeof console !== 'undefined' && IS_ABSOLUTE_SERVICE_URL) {
  console.info('[auth] transport order: ' +
    AUTH_TRANSPORTS.map(function (t) {
      return t.id + (t.credentials ? ' (cookies)' : ' (no cookies)');
    }).join(' -> '));
}

/**
 * POST to a public auth endpoint: never an Authorization header, and with cookies
 * whenever the chosen transport supports them, so the register -> verify-otp session
 * survives.
 *
 * If the request never reaches the API, the next transport is tried automatically and
 * the winner is remembered. Only transport-level failures fall through - a real API
 * error (including a JSON 404) is returned to the caller untouched.
 */
function postAuth(path, body){
  function attempt(index){
    const transport = AUTH_TRANSPORTS[index];
    if (!transport) {
      return Promise.reject(new Error('No usable connection to the Amejpay API.'));
    }
    return transport.client.post(path, body)
      .then(function (response) {
        // A static host can answer a proxy path with index.html and status 200.
        if (responseIsHtml(response)) {
          const err = new Error('html-response');
          err.response = response;
          throw err;
        }
        if (authTransportIndex !== index) {
          authTransportIndex = index;
          AUTH_BASE_URL = transport.base;
        }
        rememberTransport(transport.id);
        return response;
      })
      .catch(function (error) {
        const next = AUTH_TRANSPORTS[index + 1];
        if (next && isTransportFailure(error)) {
          console.warn('[auth] transport "' + transport.id + '" (' + transport.base +
            ') did not reach the API - trying "' + next.id + '".',
            error.response ? ('status ' + error.response.status) : (error.message || 'no response'));
          return attempt(index + 1);
        }
        if (!error.response) {
          console.error('[auth] "' + transport.id + '" could not reach ' + transport.base + path + '. ' +
            (transport.credentials
              ? 'A credentialed request is blocked unless the API returns an exact ' +
                'Access-Control-Allow-Origin plus Access-Control-Allow-Credentials: true.'
              : 'Check the API host is reachable.'));
        } else {
          console.warn('[auth] POST ' + transport.base + path + ' failed:',
            error.response.status, error.response.data, '| transport=' + transport.id);
        }
        throw error;
      });
  }
  return attempt(authTransportIndex);
}

/* ------------------------------------------------------------------ *
 * PENDING REGISTRATION CONTEXT
 *
 * auth/register returns the email the server actually parked. Everything after it
 * (verify-otp, resend-otp) must use THAT value, never what is currently typed in a
 * component - otherwise a corrected typo or a remounted component sends a different
 * address and the server rightly reports no pending registration.
 *
 * sessionStorage keeps it across a page refresh and clears itself when the tab closes.
 * ------------------------------------------------------------------ */
const PENDING_REGISTRATION_KEY = 'amejpay.pendingRegistration';

export function savePendingRegistration(email, expiresIn){
  try {
    window.sessionStorage.setItem(PENDING_REGISTRATION_KEY, JSON.stringify({
      email: email,
      expiresIn: expiresIn || 10,
      at: Date.now()
    }));
  } catch (e) { /* private mode - state still holds it for this page life */ }
}

export function readPendingRegistration(){
  try {
    const raw = window.sessionStorage.getItem(PENDING_REGISTRATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

export function clearPendingRegistration(){
  try { window.sessionStorage.removeItem(PENDING_REGISTRATION_KEY); } catch (e) { /* noop */ }
}

/** True when a 404 body means "no pending registration" rather than a missing route. */
function isRegistrationMissing(error){
  const d = error.response && error.response.data;
  if (!d) { return false; }
  const text = (typeof d === 'string') ? d : JSON.stringify(d);
  // Deliberately NOT matching a bare "not found" - DRF/Django use that for missing ROUTES.
  // "No user registered" / "user not found" are what this API returns when the pending
  // registration cannot be located, which is the session-cookie failure.
  return /registration|pending|expired|no record|otp|no user|not registered|user not found/i.test(text);
}

function parseRegisterError(error){
  const res = error.response;
  const out = { fieldErrors: {}, message: '' };
  if (!res) {
    // No response at all. This is almost never the user's connection - it is the request
    // never reaching the API: a dev proxy that is not running, or a blocked CORS
    // preflight. Say something actionable, and put the real detail in the console.
    out.message = 'Could not reach the Amejpay server. Please try again in a moment.';
    console.error('[auth] No response received. In the Network tab this shows as a failed ' +
      'request with no status code - that is a proxy or CORS problem, not connectivity.',
      error && error.message);
    return out;
  }
  const data = res.data;
  if (res.status === 409) {
    out.message = (data && (data.message || data.detail)) || 'This email is already registered. Please sign in instead.';
    out.fieldErrors.email = out.message;
    return out;
  }
  if (res.status === 429) {
    out.message = (data && (data.message || data.detail)) || 'Too many requests. Please wait a moment before trying again.';
    return out;
  }
  if (typeof data === 'string') { out.message = data; return out; }
  if (data && typeof data === 'object') {
    if (data.errors && typeof data.errors === 'object') {
      Object.keys(data.errors).forEach(function(k){
        const v = data.errors[k];
        out.fieldErrors[k] = Array.isArray(v) ? v[0] : String(v);
      });
    }
    Object.keys(data).forEach(function(k){
      if (['message','detail','success','errors','email_sent','expires_in_minutes'].indexOf(k) !== -1) { return; }
      const v = data[k];
      if (Array.isArray(v)) { out.fieldErrors[k] = String(v[0]); }
      else if (typeof v === 'string') { out.fieldErrors[k] = v; }
    });
    out.message = data.message || data.detail || '';
    if (!out.message) {
      const keys = Object.keys(out.fieldErrors);
      out.message = keys.length ? out.fieldErrors[keys[0]] : 'Something went wrong. Please try again.';
    }
  }
  if (!out.message) { out.message = 'Something went wrong. Please try again.'; }
  return out;
}

const KYC_DOC_ENDPOINTS = {
  types: 'kyc/document-types',
  list: 'kyc/documents',
  create: 'kyc/documents',
  update: 'kyc/documents/',   // + id  (PUT)
  remove: 'kyc/documents/'    // + id  (DELETE)
};

export default {
    /**
     * POST feedback - the customer's answers.
     *
     * The two emails (thank-you to the customer, full copy to the product team) are sent
     * by the BACKEND. A browser cannot speak SMTP, and any credential placed in the
     * bundle is readable by every visitor, so the frontend only submits the form and
     * reports what the server says.
     *
     * Expected: 200/201 with { message }.
     */
    submitFeedback({commit}, payload){
      // Deliberately the public client: feedback can be sent while signed out, and it
      // must not fail because a stale Bearer token was attached.
      return publicClient
      .post('feedback', payload)
      .then(response => {
        const d = response.data || {};
        return { ok: true, message: d.message || 'Thank you! Your feedback has been sent.' };
      })
      .catch(error => {
        const status = error.response && error.response.status;
        console.warn('[submitFeedback] failed:', 'feedback', status);

        // Endpoint not deployed yet - say so plainly rather than showing the customer
        // Django's debug page.
        if (isMissingEndpoint(error)) {
          return {
            ok: false,
            unavailable: true,
            message: 'The feedback service is not available yet. ' +
                     'Please email amej.product@gmail.com and we will read every word.'
          };
        }
        const parsed = parseRegisterError(error);
        return {
          ok: false,
          fieldErrors: parsed.fieldErrors,
          message: parsed.message || 'Sorry, your feedback could not be sent. Please try again.'
        };
      })
    },

    /**
     * POST subscription - { email, source }
     *
     * The catch block used to read `error.response.data.message` directly. On a network
     * failure, a CORS rejection or a 404 there IS no `error.response`, so that line threw
     * a TypeError inside the error handler itself and the screen was left with the flag
     * set and no text to show. Everything now goes through extractError, which copes with
     * a missing response, an HTML error page and a plain string body alike.
     */
    submitSubscriber({commit}, data){
      return axios
        .post('subscription', data)
        .then(response => {
          const d = response.data || {};
          // The same text is committed AND returned, so a caller reading either one
          // never ends up with an empty alert when the API omits `message`.
          if (response.status === 200 || response.status === 201) {
            const okText = d.message || 'You are subscribed. Thank you!';
            commit("setSuccessMessage", "success");
            commit("setErrorMessage", '');
            commit("setMessage", okText);
            return { ok: true, message: okText };
          }
          const badText = d.message || 'We could not add that address.';
          commit("setSuccessMessage", '');
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", badText);
          return { ok: false, message: badText };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[submitSubscriber] failed:', 'subscription', status);
          commit("setSuccessMessage", '');
          commit("setErrorMessage", 'errormessage');

          // Endpoint not deployed - say so plainly instead of showing a blank alert.
          const message = isMissingEndpoint(error)
            ? 'The newsletter service is not available yet. Please try again later.'
            : extractError(error, 'We could not add that address. Please try again.');

          commit("setMessage", message);
          return { ok: false, message: message };
        })
    },


    // NOTE: the old `verifyOtp` action was removed here. It posted to auth/verify-otp
    // through the DEFAULT axios instance, which the main.js interceptor decorates with
    // the logged-in Bearer token, and it only treated HTTP 200 as success while this
    // endpoint answers 201 Created. It had no callers. Use `userVerifyOtp` instead.



    activateaccount({commit},tokenid){
      return axios
      .post('users/activate', tokenid)
      .then(response => {
        if(response.status==200){
          commit("setAccountSuccessMessage", "success")
        }
        })
      .catch(error=>{
        if (error){
          commit("setErrorMessage", 'errormessage')
        }
      })
    },


    /**
     * Step 1 of recovery.  POST users/forgetpassword  { email }
     *
     * The screen used to collect a COUNTRY and PHONE and post
     * `{ phone, device_id }` as FormData. The endpoint reads `email`, so Django threw
     *     MultiValueDictKeyError at /v1/users/forgetpassword 'email'
     * and rendered its debug page straight into the card. Sending the documented JSON
     * body fixes it at the source.
     */
    forgetpassword({commit}, payload){
      const body = payload || {};
      commit("setSuccessMessage", '');
      commit("setErrorMessage", '');
      commit("setMessage", '');
      commit("setRegisterErrors", {});
      return publicClient
      .post('users/forgetpassword', { email: String(body.email || '').trim() })
      .then(response => {
        const data = response.data || {};
        if ((response.status === 200 || response.status === 201) && data.success !== false) {
          commit("setSuccessMessage", 'success');
          commit("setMessage", data.message || 'We have emailed you a reset code.');
          commit("setStep", 2);
          return {
            ok: true,
            message: data.message || 'We have emailed you a reset code.',
            email: data.email || body.email,
            expiresIn: data.expires_in_minutes || 10,
            fieldErrors: {}
          };
        }
        const msg = data.message || 'Unable to send a reset code.';
        commit("setErrorMessage", 'errormessage');
        commit("setMessage", msg);
        return { ok: false, message: msg, fieldErrors: {} };
      })
      .catch(error => {
        const parsed = parseRegisterError(error);
        const status = error.response && error.response.status;
        console.warn('[forgetpassword] failed:', status, error.response && error.response.data);
        // 404 here means "no account with that email", not a missing route.
        //
        // Read the SERVER's message rather than parsed.message: the parser already
        // substitutes a generic "Something went wrong" fallback, which is truthy, so
        // `parsed.message || specific` would never reach the specific text.
        const body = (error.response && error.response.data) || {};
        const serverMsg = (typeof body === 'object') ? (body.message || body.detail || '') : '';
        let msg = parsed.message;
        if (status === 404) {
          msg = serverMsg || 'No account was found with that email address.';
        }
        commit("setErrorMessage", 'errormessage');
        commit("setRegisterErrors", parsed.fieldErrors);
        commit("setMessage", msg);
        return { ok: false, message: msg, status: status, fieldErrors: parsed.fieldErrors };
      })
    },

    /**
     * Step 2 of recovery.  POST users/resetpassword
     *   { token, password, confirm_password }
     *
     * The old screen sent `otp` and `new_password` - neither of which this endpoint
     * reads - so even a correct code could not have worked.
     */
    resetpassword({commit}, payload){
      const body = payload || {};
      commit("setSuccessMessage", '');
      commit("setErrorMessage", '');
      commit("setMessage", '');
      commit("setRegisterErrors", {});
      return publicClient
      .post('users/resetpassword', {
        token: String(body.token || '').trim(),
        password: body.password,
        confirm_password: body.confirm_password
      })
      .then(response => {
        const data = response.data || {};
        if ((response.status === 200 || response.status === 201) && data.success !== false) {
          commit("setSuccessMessage", 'success');
          commit("setMessage", data.message || 'Your password has been reset.');
          return { ok: true, message: data.message || 'Your password has been reset.', fieldErrors: {} };
        }
        const msg = data.message || 'Unable to reset the password.';
        commit("setErrorMessage", 'errormessage');
        commit("setMessage", msg);
        return { ok: false, message: msg, fieldErrors: {} };
      })
      .catch(error => {
        const parsed = parseRegisterError(error);
        const status = error.response && error.response.status;
        console.warn('[resetpassword] failed:', status, error.response && error.response.data);
        const body = (error.response && error.response.data) || {};
        const serverMsg = (typeof body === 'object') ? (body.message || body.detail || '') : '';
        let msg = parsed.message;
        if (status === 400 || status === 404) {
          msg = serverMsg || 'That code is invalid or has expired. Please request a new one.';
        }
        commit("setErrorMessage", 'errormessage');
        commit("setRegisterErrors", parsed.fieldErrors);
        commit("setPasswordRestMessage", 'passwordmessage');
        commit("setMessage", msg);
        return { ok: false, message: msg, status: status, fieldErrors: parsed.fieldErrors };
      })
    },

    // listProfile({commit}){
    //     axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
    //     return axios
    //     .get('profile', {})
    //     .then(response => {
    //           commit('setProfile', response.data)
    //       })
    //     .catch(error=>{
    //       if (error.response.status==401){
    //         window.location.replace("/logout");
    //       }
    //     })
    //   },

    /**
     * The profile of the signed-in user.
     *
     * This action used to call window.location.replace("/logout") both when no token
     * was present and on ANY 401 - so a single expired access token, or a moment where
     * the store had not hydrated yet, hard-navigated the user out of the app. The 401
     * case is now owned by the refresh interceptor in main.js, which renews the token
     * and replays the request; only a genuinely absent session redirects, and it goes
     * to /login carrying where the user was headed.
     */
    listProfile({ commit }) {
      const token = getAccessToken();

      if (!token) {
        const path = window.location.pathname || '';
        console.warn('[listProfile] no session - redirecting to login');
        window.location.replace('/login?next=' + encodeURIComponent(path));
        return Promise.resolve();
      }

      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;

      return axios
        .get('profile')
        .then(response => {
          commit('setProfile', response.data);
          // `author.merchant` is authoritative for which dashboard this account uses.
          rememberRoleFromProfile(response.data);
        })
        .catch(error => {
          const status = error.response && error.response.status;
          // 401 has already been through refresh-and-retry by the time it lands here.
          console.warn('[listProfile] failed:', status || (error && error.message));
        });
    },

    listUserKyc({commit}){
      axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
      return axios
      .get(KYC_ENDPOINTS.get, {})
      .then(response => {
            commit('setKycData', response.data)
        })
      .catch(error=>{
        // Guarded: error.response can be undefined on a network failure
        const status = error.response && error.response.status;
        console.warn('[listUserKyc] request failed:', KYC_ENDPOINTS.get, status);
      })
    },

    /**
     * Submit KYC details (used when author.is_kyc_verified === false).
     * Accepts a FormData instance so the company logo can be sent with it.
     */
    createKyc({commit}, formData){
      axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
      commit("setSuccessMessage", '');
      commit("setErrorMessage", '');
      return axios
      .post(KYC_ENDPOINTS.create, formData)
      .then(response => {
        if(response.status == 200 || response.status == 201){
          commit("setSuccessMessage", 'success');
          if (response.data) { commit('setKycData', response.data); }
        }
      })
      .catch(error=>{
        const status = error.response && error.response.status;
        const msg = (error.response && error.response.data && (error.response.data.message || error.response.data.detail)) || 'Unable to submit KYC. Please try again.';
        console.warn('[createKyc] request failed:', KYC_ENDPOINTS.create, status);
        commit("setErrorMessage", 'errormessage');
        commit("setMessage", msg);
      })
    },

    /**
     * POST merchant/kyc/update-requests
     * Carries the COMPLETE merchant KYC payload (FormData, incl. certificates).
     * The admin reviews it; the new values only appear on the merchant side once approved.
     */
    requestKycUpdate({commit}, payload){
      axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
      commit("setSuccessMessage", '');
      commit("setErrorMessage", '');
      return axios
      .post(KYC_ENDPOINTS.updateRequest, payload)
      .then(response => {
        if(response.status == 200 || response.status == 201){
          commit("setSuccessMessage", 'success');
        }
      })
      .catch(error=>{
        const status = error.response && error.response.status;
        const parsed = parseRegisterError(error);
        console.warn('[requestKycUpdate] request failed:', KYC_ENDPOINTS.updateRequest, status, error.response && error.response.data);
        commit("setErrorMessage", 'errormessage');
        commit("setRegisterErrors", parsed.fieldErrors);
        commit("setMessage", parsed.message);
      })
    },

      listProfileForMerchant({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
        return axios
        .get('profile', {})
        .then(response => {
              commit('setProfile', response.data)
          })
        .catch(error=>{
          if (error.response.status==401){
            console.log("Sorry! No User Found.");
          }
        })
      },
    
      listRecentProfile({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
        return axios
        .get('profile', {})
        .then(response => {
            commit('setRecentProfile',response.data)
        })
      },


      listTransactions({ commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('transactions', {})
        .then(response => {
          commit('setTransactions',response.data)
        })
        .catch(error => {
          const url = error.config && error.config.url;
          const status = error.response && error.response.status;
          console.warn('[listTransactions] request failed:', url, status);
        })
      },

      createLoadFund({commit},fund){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('loadfund', fund)
        .then(response => {
          commit("setMessage", "");
          commit("setMessage", "success");
          commit("setfundResponse", response.data.paymentRequest);
          // state.transactions.push(response.data)
        })
        .catch(error=>{
          if (error.response.status==400){
            commit("setErrorMessage", "");
            commit("setErrorMessage", "card error");
            commit("setCardLoadErrorMessage", error.response.data.message);
          }
        })
      },

      createPayment({state,commit}, payment){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post(payment.action_url, payment)
        .then(response => {
          state.payment.push(response.data)
          commit("setTransactionid",response.data.transaction)
          commit("setAmount",response.data.amount)
          commit("setSuccessMessage", "success");
        })
        .catch(error=>{
          if (error){
            commit('setErrorMessage','errormessage')
            commit("setMessage", error.response.data.message);
          }
        })

      },

      transactionOTP({state,commit},transactionotp){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .put(transactionotp.action_url+'/confirm', transactionotp)
        .then(response => {
          if(response.status==200){
            commit('setActionUrl', transactionotp.action_url)
            commit('setOtpsuccess','success')
            state.profile.coins=state.profile.coins-transactionotp.amount
          }
        })
        .catch(error=>{
          if (error){
            commit("setOtperror", 'otperror')
            commit("setOtpMessage",error.response.data.message);
          }
        })
      },

      withdrawOTP({state,commit},withdrawotp){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .put('withdraws/confirm', withdrawotp)
        .then(response => {
          if(response.status==200){
            commit("setMessage","")
            commit('setOTPSuccessMessage','success')
            state.profile.coins=state.profile.coins-withdrawotp.amount
          }
        })
        .catch(error=>{
          if (error){
            commit("setOtperror", 'otperror')
            commit("setMessage",error.response.data.message);
          }
        })
      },



      createRequest({commit,state}, payment){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('requests',payment)
        .then(response => {
          state.requests.push(response.data)
          commit('setRequestMessage',"requestmessage");
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage",'errormessage')
            commit("setMessage", "");
            commit("setMessage", error.response.data.message);
          }
        })
      },

      listRequests({ commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('requests', {})
        .then(response => {
          commit('setRequests',response.data)
        })
      },


      createBank({state,commit}, bank){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('banks', bank)
        .then(response => {
          commit("setAddbankSuccessMessage",'success')
          state.banks.push(response.data)
        })
        .catch(error=>{
          if (error){
            commit('setErrorMessage','errormessage')
            commit("setMessage", "Swift code already exists.");
          }
        })
      },


      listBanks({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
        return axios
        .get('banks', {})
        .then(response => {
              commit('setBanks',response.data)
          })
      },

      deleteBank({commit,state}, deleteObj){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .put('banks/delete', deleteObj)
        .then(response => {
          commit('setSuccessMessage','success')
          state.banks.push(response.data);
        })
        .catch(error=>{
          if (error){
            commit('setErrorMessage','errormessage')
            commit("setMessage","Password do not match.");
          }
        })

      },

      createWithdraw({state,commit}, withdraw){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('withdraws', withdraw)
        .then(response => {
          commit("setWithdrawid",response.data.transaction)
          commit("setAmount",response.data.amount)
          commit('setSuccessMessage','success')
        })
        .catch(error=>{
          if (error){
            commit('setErrorMessage','errormessage')
            commit("setMessage", "Can not withdraw try again");
          }
        })
      },

      /**
       * POST users/changepassword - { old_password, new_password, confirm_password }
       *                          -> { message }
       *
       * Two things were wrong here:
       *   - on success it committed the flag but NOT the server's message, so the screen
       *     read whatever `message` happened to be left over from an earlier action;
       *   - it re-threw, which turned an ordinary "old password is wrong" into an
       *     unhandled rejection in the console.
       * It now resolves with { ok, message, fieldErrors } and the caller reads that
       * instead of the shared flags, which any other action can overwrite.
       */
      changepassword({commit}, password){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('users/changepassword', password)
        .then(response => {
          const d = response.data || {};
          const text = d.message || 'Password changed successfully.';
          commit("setSuccessMessage", 'success');
          commit("setErrorMessage", '');
          commit("setMessage", text);
          return { ok: true, message: text };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[changepassword] failed:', 'users/changepassword', status);
          commit("setSuccessMessage", '');
          commit("setErrorMessage", 'errormessage');

          if (isMissingEndpoint(error)) {
            const missing = 'Changing your password is not available right now. Please try again later.';
            commit("setMessage", missing);
            return { ok: false, message: missing, fieldErrors: {} };
          }

          // {"old_password": ["..."]} lands under the matching field.
          const parsed = parseRegisterError(error);
          const text = parsed.message || extractError(error, 'Unable to change your password.');
          commit("setRegisterErrors", parsed.fieldErrors);
          commit("setMessage", text);
          return { ok: false, message: text, fieldErrors: parsed.fieldErrors };
        })
      },

      listnotifications({ commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('notifications', {})
        .then(response => {
          commit('setNotifications',response.data)
        })
        .catch(error=>{
          // ✅ FIXED: Added error handling for 403/404 endpoints
          console.warn('Notifications API error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.message
          });
          // Don't crash - user might not have permission or endpoint might be removed
          commit('setNotifications', [])
          // Re-throw so component can handle if needed
          throw error;
        })
      },

      updateProfile({commit}, updateprofile){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        // Supports both a plain object and FormData (used when uploading the company logo).
        return axios
        .put('profile/update', updateprofile)
        .then(response => {
          if(response.status==200){
            commit("setSuccessMessage",'success');
          }
        })
        .catch(error=>{
          const msg = (error.response && error.response.data && (error.response.data.message || error.response.data.detail)) || 'Unable to update profile.';
          commit("setErrorMessage", 'errormessage')
          commit("setMessage", msg);
        })
      },

      createaddress({commit,state}, addresstype){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('address', addresstype)
        .then(response => {
          commit("setSuccessMessage",'success')
          state.address = response.data;
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            // commit("setMessage", error.response.data.message)
          }
        })
      },

      liststaticpage({commit},slug){
        axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
        return axios
        .get('help'+slug, {})
        .then(response => {
          commit('setstaticPage',response.data)
        })
      },

      /**
       * Mark every notification as read.  PUT markallnotifications  { status: true }
       *
       * This was calling `PUT notifications` - an endpoint that does not exist - which
       * is why the button did nothing. It also pushed the API response INTO the
       * notification list, so a success would have added a phantom row.
       */
      markallnotificationRead({state,commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer '+ this.state.token
        return axios
        .put('markallnotifications', { status: true })
        .then(() => {
          // Update in place rather than refetching: the list is already on screen.
          const list = Array.isArray(state.notifications) ? state.notifications : [];
          list.forEach(function (n) { if (n) { n.status = true; } });
          if (state.profile && state.profile.author) {
            state.profile.author.notification_count = 0;
          }
          commit("setSuccessMessage", 'success');
          return { ok: true };
        })
        .catch(error => {
          console.warn('[markallnotificationRead] failed:', error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Could not mark the notifications as read.'));
          return { ok: false };
        })
      },


      requestAccept({state,commit}, request){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('requests/accept', request)
        .then(response => {
          commit('setSuccessMessage','success');
          state.requests.push(response.data)
          commit('setRequestid',request.transaction_id)
        })
        .catch(error=>{
          if (error){
            commit("setOtperror",'otperror')
            commit("setMessage", '');
            commit("setMessage", error.response.data.message);
          }
        })
      },

      /**
       * Mark ONE notification as read.  PUT marknotifications/{id}  { status: true }
       *
       * The body was missing entirely - the endpoint requires { status } - and the
       * else-branch ran `state.profile.push(...)` on an object, which throws.
       * `notification_id` was also the wrong field name; the payload calls it `id`.
       */
      marknotificationRead({state}, notification){
        const payload = notification || {};
        const id = payload.id || payload.notification_id;
        if (!id) { return Promise.resolve({ ok: false }); }

        // Already read - nothing to send, and the count must not move again.
        if (payload.status === true) { return Promise.resolve({ ok: true, already: true }); }

        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .put('marknotifications/' + id, { status: true })
        .then(() => {
          const list = Array.isArray(state.notifications) ? state.notifications : [];
          for (let i = 0; i < list.length; i++) {
            if (list[i] && String(list[i].id) === String(id)) { list[i].status = true; break; }
          }
          const author = state.profile && state.profile.author;
          if (author && Number(author.notification_count) > 0) {
            author.notification_count = Number(author.notification_count) - 1;
          }
          return { ok: true };
        })
        .catch(error => {
          console.warn('[marknotificationRead] failed:', error.response && error.response.status);
          return { ok: false };
        })
      },

      listPhonenumber({ commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('phone', {})
        .then(response => {
          commit('setPhonenumber',response.data)
        })
      },

      addPhonenumber({commit},phonenumber){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('phone', phonenumber)
        .then(response => {
          if(response.status==200){
            commit('setSuccessMessage','success');
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", "errormessage");
            commit("setMessage", "phonenumber error");
          }
        })
      },

      removePhonenumber({state,commit},password){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('phone/delete',password)
        .then(response => {
          commit("setSuccessMessage",'success')
          state.phonenumber.push(response.data)
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", "errormessage");
          }
        })
      },

      phoneOtpSubmit({state,commit},otpcode){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('phone/confirm', otpcode)
        .then(response => {
          commit('setSuccessMessage','success')
          state.profile.push(response.data)
        })
        .catch(error=>{
          if (error){
            commit("setOtpMessage", "otpmessage");
          }
        })
      },

      requestAcceptwithotp({state,commit},requestotpid){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .put('requests/confirm', requestotpid)
        .then(response => {
          if(response.status == 200){
            state.requests.push(response.data)
            commit('setSuccessMessage','success');
            state.profile.coins=state.profile.coins-requestotpid.amount
          }else{
            commit("setOtperror",'otperror')
            commit("setMessage", "Sorry! Could not proceed.");
          }
        })
        .catch(error=>{
          if (error){
            commit("setOtperror",'otperror')
            commit("setMessage", error);
          }
        })
      },

      changePhoneNumber({commit}, phoneObject){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('phonechange', phoneObject)
        .then(response => {
          if(response.status == 200){
            commit('setSuccessMessage','success');
          }else{
            commit('setSuccessMessage','');
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", "errormessage");
            commit("setMessage", error.response.data.message);
          }
        })
      },

      confirmChangePhone({commit}, phoneObject){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('phonechange/confirm', phoneObject)
        .then(response => {
          if(response.status==200){
            commit('setSuccessMessage','success');
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", "errormessage");
            commit("setMessage", "phonenumber error");
          }
        })
      },


      /** Remove every notification.  DELETE notifications/delete */
      clearallnotification({state,commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .delete('notifications/delete')
        .then(() => {
          // Was pushing the response INTO the list it had just emptied.
          state.notifications = [];
          if (state.profile && state.profile.author) {
            state.profile.author.notification_count = 0;
          }
          commit('setSuccessMessage','success');
          return { ok: true };
        })
        .catch(error => {
          console.warn('[clearallnotification] failed:', error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Could not clear the notifications.'));
          return { ok: false };
        })
      },


      requestMerchantAccount({commit}, merchant){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('merchants', merchant)
        .then(response => {
          if(response.status==200){
            commit('setSuccessMessage','success');
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage",'errormessage')
            commit("setMessage", error.response.data.message);
          }
        })
      },


      listMerchant({ commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('merchants', {})
        .then(response => {
          commit('setMerchant',response.data)
        })
      },


      downloadQrcode({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('downloadqrcode')
        .then(response => {
          if(response.status==200){
            commit('setDownloadMessage','success')
          }
        })
      },


      listAllBank({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('adminbanks')
        .then(response => {
          if(response.status==200){
            commit('setAllBank',response.data)
          }
        })
      },


      send2c2pRequest({commit},payload){
        // Note: 'Access-Control-Allow-Origin' is a RESPONSE header set by the server,
        // not a request header. Setting it on the request does nothing and can trigger
        // an unnecessary CORS preflight. Endpoint is driven by env config.
        return axios
        .post(process.env.VUE_APP_2C2P_ENDPOINT_URL, payload)

        // .then(response => {
        //   commit("setMessage", "");
        //   commit("setMessage", "success");
        //   commit("setfundResponse", response.data.paymentRequest);
        //   // state.transactions.push(response.data)
        // })
        .catch(error=>{
          if (error.response.status==400){
            commit("setErrorMessage", "");
            commit("setErrorMessage", "card error");
            commit("setCardLoadErrorMessage", error.response.data.message);
          }
        })
      },

      listCountries({commit}){
        // NOTE: this used to commit setSuccessMessage('success'). That flag is shared with
        // every other action, so a slow countries request could land AFTER a failed
        // registration and make the UI believe the registration succeeded. Never write a
        // global result flag from a lookup call.
        return axios
        .get('countries', {})
        .then(response => {
              commit('setCountries', response.data)
          })
        .catch(error => {
          console.warn('[listCountries] failed:', error.response && error.response.status);
          commit('setCountries', []);
        })
      },

      listSQuestions({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('questions', {})
        .then(response => {
              commit('setSQuestions',response.data)
          })
        .catch(error => {
          const url = error.config && error.config.url;
          const status = error.response && error.response.status;
          console.warn('[listSQuestions] request failed (check backend route):', url, status);
        })
      },

      createQuestonAnswer({commit,state}, questionans){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('questionans', questionans)
        .then(response => {
          commit("setSuccessMessage",'success')
          state.sQuestionAnswers.push(response.data);
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      listSQuestionAnswers({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('questionans', {})
        .then(response => {
              commit('setSQuestionAnswers',response.data)
          })
        .catch(error => {
          const url = error.config && error.config.url;
          const status = error.response && error.response.status;
          console.warn('[listSQuestionAnswers] request failed (check backend route):', url, status);
        })
      },

      listDocumentTypes({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get(KYC_DOC_ENDPOINTS.types, {})
        .then(response => {
              commit('setDocumentTypes', normalizeList(response.data))
          })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[listDocumentTypes] request failed:', KYC_DOC_ENDPOINTS.types, status);
        })
      },

      createUserDocument({commit}, documents){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        // `documents` must be a FormData instance (multipart: front_image / back_image)
        return axios
        .post(KYC_DOC_ENDPOINTS.create, documents)
        .then(response => {
          if(response.status == 200 || response.status == 201){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          const status = error.response && error.response.status;
          console.warn('[createUserDocument] request failed:', KYC_DOC_ENDPOINTS.create, status);
          commit("setErrorMessage", 'errormessage')
          commit("setMessage", extractError(error, 'Failed to upload document.'))
        })
      },

      updateUserDocument({commit}, payload){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        // payload = { id, formData }
        return axios
        .put(KYC_DOC_ENDPOINTS.update + payload.id, payload.formData)
        .then(response => {
          if(response.status == 200 || response.status == 201){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          const status = error.response && error.response.status;
          console.warn('[updateUserDocument] request failed:', KYC_DOC_ENDPOINTS.update + payload.id, status);
          commit("setErrorMessage", 'errormessage')
          commit("setMessage", extractError(error, 'Failed to update document.'))
        })
      },

      listUserDocuments({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get(KYC_DOC_ENDPOINTS.list, {})
        .then(response => {
              commit('setUserDocuments', normalizeList(response.data))
          })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[listUserDocuments] request failed:', KYC_DOC_ENDPOINTS.list, status);
          commit('setUserDocuments', []);
        })
      },

      removeUserDocument({state, commit}, documentid){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        return axios
        .delete(KYC_DOC_ENDPOINTS.remove + documentid)
        .then(() => {
          commit("setSuccessMessage",'success')
          // BUGFIX: previously PUSHED the response onto the list on delete.
          const remaining = (state.userDocuments || []).filter(function(doc){
            return String(doc.id) !== String(documentid);
          });
          commit('setUserDocuments', remaining);
        })
        .catch(error=>{
          const status = error.response && error.response.status;
          console.warn('[removeUserDocument] request failed:', KYC_DOC_ENDPOINTS.remove + documentid, status);
          commit("setErrorMessage", 'errormessage')
          commit("setMessage", extractError(error, 'Failed to delete document.'))
        })
      },

      listTimezones({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('timezones', {})
        .then(response => {
              commit('setTimezones',response.data)
          })
        .catch(error => {
          const url = error.config && error.config.url;
          const status = error.response && error.response.status;
          console.warn('[listTimezones] request failed (check backend route):', url, status);
        })
      },

      /**
       * Exchange rates. GET currency/rates
       *
       * Cached in localStorage for 24 hours: the backend recalculates daily, and every
       * screen that shows money needs this table, so re-fetching per page would be a
       * request per navigation for data that cannot have changed.
       *
       * Pass { force: true } to bypass the cache.
       */
      listCurrencies({commit}, options){
        const opts = options || {};
        const cached = opts.force ? null : getCachedRates();
        if (cached) {
          commit('setCurrencies', cached);
          return Promise.resolve(cached);
        }

        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('currency/rates', {})
        .then(response => {
          const data = response.data || {};
          cacheRates(data);
          commit('setCurrencies', data);
          return data;
        })
        .catch(error => {
          const url = error.config && error.config.url;
          const status = error.response && error.response.status;
          console.warn('[listCurrencies] request failed (check backend route):', url, status);
          // A stale table beats an empty one - every amount on screen depends on it.
          const stale = getAnyCachedRates();
          if (stale) {
            console.warn('[listCurrencies] falling back to the cached table');
            commit('setCurrencies', stale);
            return stale;
          }
          return null;
        })
      },

      /**
       * Countries and the currency each one should be displayed in.
       * GET currency/countries - cached alongside the rates.
       */
      listCurrencyCountries({commit}, options){
        const opts = options || {};
        const cached = opts.force ? null : getCachedCountries();
        if (cached) {
          commit('setCurrencyCountries', cached);
          return Promise.resolve(cached);
        }

        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get('currency/countries', {})
        .then(response => {
          const list = cacheCountries(response.data);
          commit('setCurrencyCountries', list || []);
          return list;
        })
        .catch(error => {
          console.warn('[listCurrencyCountries] failed:', error.response && error.response.status);
          const stale = getAnyCachedCountries();
          if (stale) { commit('setCurrencyCountries', stale); return stale; }
          return null;
        })
      },

      /**
       * An authoritative quote. POST currency/convert
       *   { from_currency, to_currency, amount }
       *   -> { base_rate, converted_amount, markup_percent, final_amount }
       *
       * Use this whenever the number is TRANSACTIONAL. The local maths in
       * utils/currency.js is for display equivalents only - it uses the mid rate and
       * does not apply the markup, so it must never be the basis of a real transfer.
       */
      convertCurrency({commit}, payload){
        const body = payload || {};
        commit("setErrorMessage", '');
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post('currency/convert', {
          from_currency: body.from_currency || body.from,
          to_currency: body.to_currency || body.to,
          amount: body.amount
        })
        .then(response => {
          const d = response.data || {};
          return {
            ok: true,
            from: d.from_currency,
            to: d.to_currency,
            amount: Number(d.amount),
            baseRate: Number(d.base_rate),
            converted: Number(d.converted_amount),
            markupPercent: Number(d.markup_percent),
            finalAmount: Number(d.final_amount)
          };
        })
        .catch(error => {
          const msg = extractError(error, 'Unable to convert that amount right now.');
          console.warn('[convertCurrency] failed:', error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", msg);
          return { ok: false, message: msg };
        })
      },

      listAdminBanks({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('adminbanks', {})
        .then(response => {
              commit('setAdminBanks',response.data)
          })
      },

      createUserBanks({commit,state}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post(`banks`, formData)
        .then(response => {
          commit("setSuccessMessage",'success')
          state.userBanks.push(response.data);
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      updateUserBanks({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .put('banks/update/'+formData.id, formData)
        .then(response => {
          commit("setSuccessMessage",'');
          if(response.status==200){
            commit("setSuccessMessage",'success');
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      listUserBanks({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('banks', {})
        .then(response => {
              commit('setUserBanks',response.data)
          })
        .catch(error => {
          const url = error.config && error.config.url;
          const status = error.response && error.response.status;
          console.warn('[listUserBanks] request failed:', url, status);
        })
      },

      requestAdminBanks({commit,state}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post(`adminbanks`, formData)
        .then(response => {
          commit("setSuccessMessage",'success')
          state.adminBanks.push(response.data);
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      generatePayuHash({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post(`payuhash`, formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
            commit("setPayuHash",response.data)
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      loadPayuMoney({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post(`payuload`, formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      loadAuthrozeNetMoney({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('authorizenet', formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
          }
        })
      },

      loadStripeMoney({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('stripeload', formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },

      /**
       * Permanently deletes the signed-in user's account.
       *   DELETE /v1/account/delete   body: { password }
       *
       * The password is re-checked by the SERVER; the modal only stops the obvious
       * accidents. Axios needs `data` for a DELETE body - passing it as the second
       * argument (as with post/put) silently sends nothing, which would have the
       * server reject every attempt as a missing password.
       */
      deleteAccount({commit}, payload){
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + (this.state.token || window.localStorage.getItem('token'));
        return axios
        .delete(ACCOUNT_ENDPOINTS.remove, { data: { password: (payload && payload.password) || '' } })
        .then(response => {
          const data = response.data || {};
          if ((response.status === 200 || response.status === 202 || response.status === 204) && data.success !== false) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", data.message || 'Your account has been deleted.');
            return { ok: true, message: data.message || 'Your account has been deleted.' };
          }
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", data.message || 'Unable to delete the account.');
          return { ok: false, message: data.message || 'Unable to delete the account.' };
        })
        .catch(error => {
          const res = error.response;
          const body = (res && res.data) || {};
          let msg = body.message || body.detail || 'Unable to delete the account.';
          if (res && res.status === 400) { msg = body.message || body.detail || 'That password is not correct.'; }
          if (res && res.status === 401) { msg = 'That password is not correct.'; }
          if (res && res.status === 403) { msg = body.message || body.detail || 'This account cannot be deleted right now.'; }
          console.warn('[deleteAccount] failed:', res && res.status, body);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", msg);
          return { ok: false, message: msg, status: res && res.status };
        })
      },

      deactivateAccount({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('deactivateaccount')
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },

      /** STEP 1 - validate details and email an OTP. No account created yet. */
      userRegister({commit}, payload){
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        commit("setRegisterErrors", {});
        return postAuth(AUTH_ENDPOINTS.register, payload)
        .then(response => {
          const data = response.data || {};
          if ((response.status == 200 || response.status == 201) && data.success !== false) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", data.message || 'Verification code sent to your email.');
            // The server echoes back the address it parked. Trust that, not the typed
            // value, and persist it so verify/resend can never drift from it.
            const registeredEmail = data.email || payload.email;
            const expiresIn = data.expires_in_minutes || 10;
            commit("setRegisterEmail", registeredEmail);
            commit("setOtpExpiresIn", expiresIn);
            savePendingRegistration(registeredEmail, expiresIn);
            // Resolve with the result so the component never depends on shared flags.
            return { ok: true, message: data.message || 'Verification code sent to your email.',
                     email: registeredEmail, expiresIn: expiresIn, fieldErrors: {} };
          }
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", data.message || 'Unable to register. Please try again.');
          return { ok: false, message: data.message || 'Unable to register. Please try again.', fieldErrors: {} };
        })
        .catch(error => {
          const parsed = parseRegisterError(error);
          console.warn('[userRegister] failed:', AUTH_ENDPOINTS.register, error.response && error.response.status, error.response && error.response.data);
          commit("setErrorMessage", 'errormessage');
          commit("setRegisterErrors", parsed.fieldErrors);
          commit("setMessage", parsed.message);
          return { ok: false, message: parsed.message, fieldErrors: parsed.fieldErrors };
        })
      },

      /** STEP 2 - verify the OTP; this is what actually creates the account. */
      userVerifyOtp({commit, state}, payload){
        commit("setRegisterStatus", null);
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        commit("setRegisterErrors", {});
        // Always verify against the address auth/register actually accepted.
        const pending = readPendingRegistration();
        const body = {
          email: (pending && pending.email) || state.registerEmail || (payload && payload.email),
          otp_code: String((payload && payload.otp_code) || '').trim()
        };
        return postAuth(AUTH_ENDPOINTS.verifyOtp, body)
        .then(response => {
          const data = response.data || {};
          if ((response.status == 200 || response.status == 201) && data.success !== false) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", data.message || 'Account created successfully!');
            if (data.user) { commit('setProfile', { author: data.user }); }
            clearPendingRegistration();
            return { ok: true, message: data.message || 'Account created successfully!',
                     user: data.user || null, tokens: data.tokens || null, fieldErrors: {} };
          }
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", data.message || 'Invalid verification code.');
          return { ok: false, message: data.message || 'Invalid verification code.', fieldErrors: {} };
        })
        .catch(error => {
          const parsed = parseRegisterError(error);
          const status = error.response && error.response.status;
          const body = error.response && error.response.data;
          console.warn('[userVerifyOtp] failed:', AUTH_ENDPOINTS.verifyOtp, status, body);
          commit("setErrorMessage", 'errormessage');
          commit("setRegisterErrors", parsed.fieldErrors);
          commit("setRegisterStatus", status || 0);
          let msg;
          if (status === 404) {
            if (isRegistrationMissing(error)) {
              msg = parsed.message ||
                'Your registration session has expired. Please request a new code or register again.';
              if (onCookielessTransport()) {
                console.error(
                  '[auth] verify-otp could not find the pending registration, and this build is ' +
                  'running on the COOKIELESS transport (base: "' + AUTH_BASE_URL + '").\n' +
                  '       auth/register stored the details against a session cookie the browser ' +
                  'was never able to send back, so the account cannot be created.\n' +
                  '       Fix ONE of these on the server (see LIVE_REGISTRATION_FIX.md):\n' +
                  '         a) reverse-proxy ' + AUTH_PROXY_PREFIX + ' on this host to the API, or\n' +
                  '         b) return an exact Access-Control-Allow-Origin plus ' +
                  'Access-Control-Allow-Credentials: true, or\n' +
                  '         c) key the pending registration by EMAIL instead of the session.'
                );
              }
            } else {
              msg = 'Verification failed (404). Please request a new code and try again.';
            }
          } else if (status === 400) {
            msg = parsed.message || 'Invalid or expired verification code.';
          } else {
            msg = parsed.message || 'Unable to verify the code. Please try again.';
          }
          commit("setMessage", msg);
          return { ok: false, message: msg, status: status, fieldErrors: parsed.fieldErrors };
        })
      },

      /** STEP 3 - resend the OTP (server enforces a 2 minute cooldown). */
      userResendOtp({commit, state}, payload){
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        const pending = readPendingRegistration();
        const body = {
          email: (pending && pending.email) || state.registerEmail || (payload && payload.email)
        };
        return postAuth(AUTH_ENDPOINTS.resendOtp, body)
        .then(response => {
          const data = response.data || {};
          if ((response.status == 200 || response.status == 201) && data.success !== false) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", data.message || 'New verification code sent to your email.');
            commit("setOtpExpiresIn", data.expires_in_minutes || 10);
            return { ok: true, message: data.message || 'New verification code sent to your email.' };
          }
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", data.message || 'Unable to resend the code.');
          return { ok: false, message: data.message || 'Unable to resend the code.' };
        })
        .catch(error => {
          const parsed = parseRegisterError(error);
          console.warn('[userResendOtp] failed:', AUTH_ENDPOINTS.resendOtp, error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", parsed.message);
          return { ok: false, message: parsed.message };
        })
      },

      /**
       * Submit a new Merchant application (public form - prospective merchant, no account yet).
       * `payload` is a plain JSON object matching the POST merchant/apply schema.
       */
      submitMerchantApply({commit}, payload){
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        commit("setRegisterErrors", {});
        return publicClient
        .post(MERCHANT_APPLY_ENDPOINT, payload)
        .then(response => {
          const data = response.data || {};
          if ((response.status == 200 || response.status == 201) && data.success !== false) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", data.message || 'Your merchant application has been submitted.');
          } else {
            commit("setErrorMessage", 'errormessage');
            commit("setMessage", data.message || 'Unable to submit the application.');
          }
        })
        .catch(error => {
          const parsed = parseRegisterError(error);
          const status = error.response && error.response.status;
          console.warn('[submitMerchantApply] failed:', MERCHANT_APPLY_ENDPOINT, status, error.response && error.response.data);
          commit("setErrorMessage", 'errormessage');
          commit("setRegisterErrors", parsed.fieldErrors);
          commit("setMessage", parsed.message);
        })
      },

      // ------------------------------------------------------------------
    // RETIRED with the old phone-first registration chain:
    //   registerOtp   -> POST registerotp  (that endpoint no longer exists)
    //   resendOtp     -> legacy phone OTP resend
    //   registeruser  -> POST auth/register, and it ran `state.profile.push(...)`,
    //                    which is what could leave `profile` array-shaped instead of
    //                    an object and quietly break anything reading profile.author
    // The live equivalents are userRegister / userVerifyOtp / userResendOtp.
    // ------------------------------------------------------------------

    registerMerchant({commit}, formData){
        return axios
        .post('merchant', formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },

      updateMerchantDetail({commit}, formData){
        return axios
        .put('merchant/'+formData.merchantid, formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },

      // Merchant Area
      listAllMerchants({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('merchant', {})
        .then(response => {
              commit('setMerchants',response.data)
          })
        .catch(error=>{
          // ✅ FIXED: Added error handling for 403/404 endpoints
          console.warn('Merchants API error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.message
          });
          // Don't crash - endpoint might be removed or user might lack permission
          commit('setMerchants', [])
          // Re-throw so component can handle if needed
          throw error;
        })
      },

      getMerchantDocuments({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('merchantdocument', {})
        .then(response => {
              commit('setMerchantDocument',response.data)
          })
      },

      postMerchantDocuments({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('merchantdocument', formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },

      deleteMerchantDocuments({commit}, docId){
        return axios
        .delete('merchantdocument/'+docId)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },


      getMerchantPaymentDetailByTransactionId({commit}, transactionId){
        return axios
        .get('merchantpaymentdetail/'+transactionId, {})
        .then(response => {
              commit('setMerchantPaymentDetail',response.data)
          })
      },

      /**
       * POST payment/initiate - creates the order and returns the gateway credentials.
       * `payload` = { gateway, amount, purpose, remarks }
       */
      initiatePayment({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        const body = {
          gateway: (payload && payload.gateway) || 'stripe',
          amount: payload && payload.amount,
          purpose: (payload && payload.purpose) || PAYMENT_GATEWAYS.defaultPurpose,
          remarks: (payload && payload.remarks) || ''
        };
        return axios
        .post(PAYMENT_GATEWAYS.initiate, body)
        .then(response => {
          const d = response.data || {};
          commit('setPaymentIntent', d);
          if (!d.client_secret || !d.publishable_key) {
            return { ok: false, message: 'The payment could not be started (missing gateway keys).' };
          }
          return { ok: true, intent: d };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          console.warn('[initiatePayment] failed:', PAYMENT_GATEWAYS.initiate, status, data);
          const parsed = parseRegisterError(error);
          return { ok: false, message: parsed.message, fieldErrors: parsed.fieldErrors, status: status };
        })
      },

      /** GET /v1/mpin - is an MPIN already set for this account? */
      getMpinStatus({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(USER_MPIN.status, {})
        .then(response => {
          const d = response.data || {};
          commit('setMpinStatus', d);
          return { ok: true, hasMpin: d.has_mpin === true };
        })
        .catch(error => {
          console.warn('[getMpinStatus] failed:', USER_MPIN.status, error.response && error.response.status);
          commit('setMpinStatus', {});
          return { ok: false, message: extractError(error, 'Unable to check your MPIN status.') };
        })
      },

      /** POST /v1/mpin/set - authorised by the ACCOUNT PASSWORD (first-time set or reset). */
      setMpin({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_MPIN.set, payload)
        .then(response => {
          const d = response.data || {};
          if (d.success === false) {
            return { ok: false, message: d.message || 'Unable to set your MPIN.' };
          }
          return { ok: true, message: d.message || 'MPIN set successfully.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          console.warn('[setMpin] failed:', USER_MPIN.set, status, data);
          const parsed = parseRegisterError(error);
          return {
            ok: false,
            message: (data && data.message) || parsed.message,
            fieldErrors: parsed.fieldErrors,
            status: status,
            // 401 here means the ACCOUNT PASSWORD was wrong, not the MPIN.
            passwordError: status === 401
          };
        })
      },

      /** POST /v1/mpin/change - authorised by the CURRENT MPIN. */
      changeMpin({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_MPIN.change, payload)
        .then(response => {
          const d = response.data || {};
          if (d.success === false) {
            return { ok: false, message: d.message || 'Unable to change your MPIN.' };
          }
          return { ok: true, message: d.message || 'MPIN changed successfully.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          console.warn('[changeMpin] failed:', USER_MPIN.change, status, data);
          const parsed = parseRegisterError(error);
          const msg = (data && data.message) || parsed.message;
          return {
            ok: false,
            message: msg,
            fieldErrors: parsed.fieldErrors,
            status: status,
            // 401 -> the current MPIN was wrong
            oldMpinError: status === 401,
            // 400 with a reuse message -> the new MPIN was used recently
            reuseError: /reuse|recent/i.test(String(msg))
          };
        })
      },

      /**
       * POST /v1/mpin/verify - read-only check.
       * NOTE: a wrong PIN still answers HTTP 200 with { valid: false }, so the caller
       * must look at `valid`, never just the status code.
       */
      verifyMpin({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_MPIN.verify, payload)
        .then(response => {
          const d = response.data || {};
          return {
            ok: d.success !== false,
            valid: d.valid === true,
            message: d.message || (d.valid === true ? 'MPIN is correct.' : 'MPIN is incorrect.')
          };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          console.warn('[verifyMpin] failed:', USER_MPIN.verify, status, data);
          return {
            ok: false,
            valid: false,
            message: (data && data.message) || extractError(error, 'Unable to verify your MPIN.'),
            notSet: status === 400 && /no mpin/i.test(String(data && data.message))
          };
        })
      },

      /** GET /v1/kyc - the signed-in user's KYC status, limits and permissions. */
      getUserKyc({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(USER_KYC.get, {})
        .then(response => {
          const d = response.data || {};
          commit('setUserKyc', d);
          return { ok: true, kyc: d };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[getUserKyc] failed:', USER_KYC.get, status);
          commit('setUserKyc', {});
          return { ok: false, message: extractError(error, 'Unable to load your KYC details.') };
        })
      },

      /** POST /v1/kyc - submit user KYC. `formData` must be a FormData (it carries 3 images). */
      submitUserKyc({commit}, formData){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_KYC.create, formData)
        .then(response => {
          const d = response.data || {};
          if (response.status === 200 || response.status === 201) {
            return { ok: true, message: d.message || 'Your KYC has been submitted for review.', kyc: d };
          }
          return { ok: false, message: d.message || 'Unable to submit your KYC.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[submitUserKyc] failed:', USER_KYC.create, status, error.response && error.response.data);
          const parsed = parseRegisterError(error);
          return { ok: false, message: parsed.message, fieldErrors: parsed.fieldErrors, status: status };
        })
      },

      /**
       * POST /v1/kyc/update-requests
       * Carries the COMPLETE user KYC payload (FormData, incl. selfie/front/back).
       * The admin reviews it; the new values only appear once approved.
       */
      requestUserKycUpdate({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_KYC.updateRequest, payload)
        .then(response => {
          const d = response.data || {};
          return { ok: true, message: d.message || 'Your update request has been sent for review.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[requestUserKycUpdate] failed:', USER_KYC.updateRequest, status, error.response && error.response.data);
          const parsed = parseRegisterError(error);
          return { ok: false, message: parsed.message, fieldErrors: parsed.fieldErrors, status: status };
        })
      },

      /** GET adminbanks - the catalogue of banks a user can add. */
      listAdminBankList({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(USER_BANKS.admin, {})
        .then(response => {
          const d = response.data;
          const rows = Array.isArray(d) ? d : (Array.isArray(d && d.results) ? d.results : []);
          commit('setAdminBanks', rows);
          return { ok: true, rows: rows };
        })
        .catch(error => {
          console.warn('[listAdminBankList] failed:', USER_BANKS.admin, error.response && error.response.status);
          commit('setAdminBanks', []);
          return { ok: false, message: extractError(error, 'Unable to load the bank list.') };
        })
      },

      /** GET banks - the user's own saved accounts. */
      listMyBanks({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(USER_BANKS.list, {})
        .then(response => {
          const d = response.data;
          const rows = Array.isArray(d) ? d : (Array.isArray(d && d.results) ? d.results : []);
          commit('setUserBanks', rows);
          return { ok: true, rows: rows };
        })
        .catch(error => {
          console.warn('[listMyBanks] failed:', USER_BANKS.list, error.response && error.response.status);
          commit('setUserBanks', []);
          return { ok: false, message: extractError(error, 'Unable to load your bank accounts.') };
        })
      },

      /** POST banks */
      addMyBank({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_BANKS.create, payload)
        .then(response => {
          const d = response.data || {};
          return { ok: true, bank: d, message: 'Bank account added.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[addMyBank] failed:', USER_BANKS.create, status, error.response && error.response.data);
          const parsed = parseRegisterError(error);
          return { ok: false, message: parsed.message, fieldErrors: parsed.fieldErrors, status: status };
        })
      },

      /** GET banks/{id} */
      getMyBank({commit}, id){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(USER_BANKS.detail(id), {})
        .then(response => {
          const d = response.data || {};
          commit('setUserBankDetail', d);
          return { ok: true, bank: d };
        })
        .catch(error => {
          console.warn('[getMyBank] failed:', USER_BANKS.detail(id), error.response && error.response.status);
          commit('setUserBankDetail', {});
          return { ok: false, message: extractError(error, 'Unable to load this bank account.') };
        })
      },

      /** DELETE banks/{id} - answers 204 No Content. */
      removeMyBank({commit}, id){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .delete(USER_BANKS.remove(id))
        .then(response => {
          if (response.status === 200 || response.status === 204) {
            return { ok: true, message: 'Bank account removed.' };
          }
          return { ok: false, message: 'Unable to remove this bank account.' };
        })
        .catch(error => {
          console.warn('[removeMyBank] failed:', USER_BANKS.remove(id), error.response && error.response.status);
          return { ok: false, message: extractError(error, 'Unable to remove this bank account.') };
        })
      },

      /**
       * GET withdraws
       *
       * The endpoint answers with a PLAIN ARRAY - no count/next envelope, no documented
       * query parameters - so the screen pages and filters in the browser. `params` is
       * forwarded anyway (empty keys dropped) so that the day the API grows ?status= /
       * ?limit= / ?offset=, this action already speaks it and only the component's
       * paging source has to change.
       */
      listUserWithdraws({commit}, params){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        const query = {};
        const given = params || {};
        Object.keys(given).forEach(function(key){
          const value = given[key];
          if (value !== undefined && value !== null && value !== '') { query[key] = value; }
        });
        return axios
        .get(USER_WITHDRAWS.list, { params: query })
        .then(response => {
          const d = response.data;
          const rows = Array.isArray(d) ? d : (Array.isArray(d && d.results) ? d.results : []);
          commit('setUserWithdraws', rows);
          return { ok: true, rows: rows };
        })
        .catch(error => {
          console.warn('[listUserWithdraws] failed:', USER_WITHDRAWS.list, error.response && error.response.status);
          commit('setUserWithdraws', []);
          return { ok: false, message: extractError(error, 'Unable to load withdrawals.') };
        })
      },

      /**
       * POST withdraws - { bank, amount, remarks }
       *
       * Returns at least { message, transaction, amount }. Since the charge model was
       * introduced the server also works out the deductions, so the whole body is passed
       * back untouched as `raw` and the known charge fields are lifted to the top level.
       * Every one of them is OPTIONAL: the confirm dialog shows a breakdown only for the
       * fields that actually arrived, so a server that answers with the old short body
       * still works exactly as before instead of rendering "$ 0.00" charges.
       */
      createUserWithdraw({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_WITHDRAWS.create, payload)
        .then(response => {
          const d = response.data || {};
          return {
            ok: true,
            message: d.message || 'Verification code sent.',
            transaction: d.transaction,
            amount: d.amount !== undefined ? d.amount : payload.amount,
            fee: d.fee,
            conversion_fee: d.conversion_fee,
            conversion_percent: d.conversion_percent,
            total_fee: d.total_fee,
            net_amount: d.net_amount,
            payout_currency: d.payout_currency,
            exchange_rate: d.exchange_rate,
            buy_rate: d.buy_rate,
            sell_rate: d.sell_rate,
            payout_amount: d.payout_amount,
            raw: d
          };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[createUserWithdraw] failed:', USER_WITHDRAWS.create, status, error.response && error.response.data);
          const raw = extractError(error, 'Unable to start this withdrawal.');
          return {
            ok: false, message: raw, status: status,
            insufficientBalance: /insufficient|not enough|balance|fund/i.test(String(raw))
          };
        })
      },

      /** PUT withdraws/confirm - { transaction_id, otp, password } */
      confirmUserWithdraw({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .put(USER_WITHDRAWS.confirm, {
          transaction_id: payload.transaction_id,
          otp: payload.otp,
          password: payload.password
        })
        .then(response => {
          const d = response.data || {};
          return { ok: true, message: d.message || 'Withdrawal successful.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[confirmUserWithdraw] failed:', USER_WITHDRAWS.confirm, status, error.response && error.response.data);
          const raw = extractError(error, 'Unable to complete this withdrawal.');
          const text = String(raw);
          return {
            ok: false, message: raw, status: status,
            otpError: /otp|code|expired|invalid/i.test(text),
            passwordError: /password|credential/i.test(text),
            insufficientBalance: /insufficient|not enough|balance|fund/i.test(text)
          };
        })
      },

      /** GET requests - all payment requests, incoming and outgoing. */
      listPaymentRequests({commit}){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(USER_REQUESTS.list, {})
        .then(response => {
          const d = response.data;
          const rows = Array.isArray(d) ? d : (Array.isArray(d && d.results) ? d.results : []);
          commit('setPaymentRequests', rows);
          return { ok: true, rows: rows };
        })
        .catch(error => {
          console.warn('[listPaymentRequests] failed:', USER_REQUESTS.list, error.response && error.response.status);
          commit('setPaymentRequests', []);
          return { ok: false, message: extractError(error, 'Unable to load payment requests.') };
        })
      },

      /** POST requests - { amount, to, remarks, date } */
      createPaymentRequest({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_REQUESTS.create, payload)
        .then(response => {
          const d = response.data || {};
          return { ok: true, message: d.message || 'Your payment request has been sent.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[createPaymentRequest] failed:', USER_REQUESTS.create, status, error.response && error.response.data);
          return { ok: false, message: extractError(error, 'Unable to send this request.'), status: status };
        })
      },

      /**
       * POST requests/respond - { transaction_id, date, status }
       * status 'rejected' finishes immediately; 'accepted' triggers an OTP email.
       */
      respondPaymentRequest({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_REQUESTS.respond, {
          transaction_id: payload.transaction_id,
          date: payload.date,
          status: payload.status
        })
        .then(response => {
          const d = response.data || {};
          return {
            ok: true,
            message: d.message || 'Request updated.',
            requestid: d.requestid || payload.transaction_id
          };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[respondPaymentRequest] failed:', USER_REQUESTS.respond, status, error.response && error.response.data);
          const raw = extractError(error, 'Unable to respond to this request.');
          return {
            ok: false, message: raw, status: status,
            insufficientBalance: /insufficient|not enough|balance|fund/i.test(String(raw))
          };
        })
      },

      /** PUT requests/confirm - { transaction_id, otp, password } */
      confirmPaymentRequest({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .put(USER_REQUESTS.confirm, {
          transaction_id: payload.transaction_id,
          otp: payload.otp,
          password: payload.password
        })
        .then(response => {
          const d = response.data || {};
          return { ok: true, message: d.message || 'You have accepted the request.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[confirmPaymentRequest] failed:', USER_REQUESTS.confirm, status, error.response && error.response.data);
          const raw = extractError(error, 'Unable to confirm this request.');
          const text = String(raw);
          return {
            ok: false, message: raw, status: status,
            otpError: /otp|code|expired|invalid/i.test(text),
            passwordError: /password|credential/i.test(text),
            insufficientBalance: /insufficient|not enough|balance|fund/i.test(text)
          };
        })
      },

      /** GET recipient?email= - verify the receiver exists before showing the confirm popup. */
      lookupRecipient({commit}, email){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit('setRecipient', {});
        return axios
        .get(USER_PAY.recipient, { params: { email: email } })
        .then(response => {
          const d = response.data || {};
          commit('setRecipient', d);
          return { ok: !!(d && d.email), recipient: d };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[lookupRecipient] failed:', USER_PAY.recipient, status);
          const msg = (status === 404)
            ? 'No Amejpay account found for this email address.'
            : extractError(error, 'Unable to verify this recipient.');
          commit('setRecipient', {});
          return { ok: false, message: msg };
        })
      },

      /**
       * GET pay - the user's payment list.
       * Sends limit/offset so the API can paginate. If it answers with a plain array
       * (no pagination support) the caller falls back to paging in the browser, which
       * still keeps the rendered table small.
       */
      listUserPayments({commit}, params){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        const query = {};
        if (params && params.limit != null) { query.limit = params.limit; }
        if (params && params.offset != null) { query.offset = params.offset; }
        if (params && params.search) { query.search = params.search; }
        return axios
        .get(USER_PAY.list, { params: query })
        .then(response => {
          const d = response.data;
          const paginated = !!(d && !Array.isArray(d) && Array.isArray(d.results));
          const rows = Array.isArray(d) ? d : (paginated ? d.results : []);
          commit('setUserPayments', rows);
          return {
            ok: true,
            rows: rows,
            serverPaginated: paginated,
            count: paginated ? (d.count || rows.length) : rows.length
          };
        })
        .catch(error => {
          console.warn('[listUserPayments] failed:', USER_PAY.list, error.response && error.response.status);
          commit('setUserPayments', []);
          return { ok: false, message: extractError(error, 'Unable to load payments.') };
        })
      },

      /** POST pay - starts the payment and triggers the OTP email. */
      createUserPayment({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_PAY.create, payload)
        .then(response => {
          const d = response.data || {};
          return {
            ok: true,
            message: d.message || 'Verification code sent.',
            transaction: d.transaction,
            amount: d.amount !== undefined ? d.amount : payload.amount
          };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          console.warn('[createUserPayment] failed:', USER_PAY.create, status, data);
          const raw = extractError(error, 'Unable to start this payment.');
          // Surface an insufficient-balance rejection distinctly so the UI can explain it.
          const insufficient = /insufficient|not enough|balance|fund/i.test(String(raw));
          return { ok: false, message: raw, status: status, insufficientBalance: insufficient };
        })
      },

      /** PUT pay/confirm - { transaction_id, otp, password } */
      confirmUserPayment({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .put(USER_PAY.confirm, {
          transaction_id: payload.transaction_id,
          otp: payload.otp,
          password: payload.password
        })
        .then(response => {
          const d = response.data || {};
          return { ok: true, message: d.message || 'Transaction successful.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          console.warn('[confirmUserPayment] failed:', USER_PAY.confirm, status, data);
          const raw = extractError(error, 'Unable to complete this payment.');
          const text = String(raw);
          return {
            ok: false,
            message: raw,
            status: status,
            otpError: /otp|code|expired|invalid/i.test(text),
            passwordError: /password|credential/i.test(text),
            insufficientBalance: /insufficient|not enough|balance|fund/i.test(text)
          };
        })
      },

      /**
       * POST payment-issue - report a mistaken payment.
       * NOTE: endpoint path not confirmed yet; change USER_PAY.issue in one place.
       */
      submitPaymentIssue({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .post(USER_PAY.issue, payload)
        .then(response => {
          const d = response.data || {};
          return { ok: true, message: d.message || 'Your issue has been submitted.' };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[submitPaymentIssue] failed:', USER_PAY.issue, status);

          // The reporting endpoint is not built yet. Say so plainly and hand over a
          // route that does work, instead of printing Django's debug page.
          if (isMissingEndpoint(error)) {
            return {
              ok: false,
              unavailable: true,
              message: 'Reporting a payment issue is not available yet. ' +
                       'Please email support@amejpay.com with the transaction reference ' +
                       'and we will look into it.'
            };
          }
          return { ok: false, message: extractError(error, 'Unable to submit this issue. Please try again.') };
        })
      },

      /** GET merchant/withdraws - list, limits and the available filter values. */
      listMerchantWithdraws({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(MERCHANT_WITHDRAWS.list, { params: buildWithdrawQuery(filters) })
        .then(response => {
          const d = response.data || {};
          commit('setMerchantWithdraws', {
            count: d.count || 0,
            limit: d.limit || (filters && filters.limit) || 20,
            offset: d.offset || 0,
            filters: d.filters || {},
            withdraw_limits: d.withdraw_limits || {},
            // The endpoint returns more than a page of rows: the caps, the fee bands, the
            // destinations and the funds all ride along, so no second call is needed.
            withdraw_fees: d.withdraw_fees || {},
            settlement_banks: Array.isArray(d.settlement_banks) ? d.settlement_banks : [],
            funds: d.funds || {},
            results: Array.isArray(d.results) ? d.results : []
          });
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[listMerchantWithdraws] failed:', MERCHANT_WITHDRAWS.list, status);
          commit('setMerchantWithdraws', { count: 0, limit: 20, offset: 0, filters: {},
            withdraw_limits: {}, withdraw_fees: {}, settlement_banks: [], funds: {}, results: [] });
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to load withdrawals.'));
        })
      },

      /**
       * POST merchant/withdraws/quote - { amount (CENTS), bank? }
       *
       * Prices a payout before it is raised. Read-only: the same pricing that
       * POST merchant/withdraws runs, so the confirm screen and the real request agree,
       * but nothing is frozen here - re-quote if the merchant pauses before confirming.
       *
       * Deliberately does NOT commit. A quote is transient screen state, and parking it
       * in the store would let a stale price survive a route change and be shown against
       * a different amount.
       */
      quoteMerchantWithdraw({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        const body = { amount: payload.amount };
        // `bank` is optional - omitted means "pay to my KYC settlement account".
        if (payload.bank !== undefined && payload.bank !== null && payload.bank !== '') {
          body.bank = payload.bank;
        }
        return axios
        .post(MERCHANT_WITHDRAWS.quote, body)
        .then(response => {
          const d = response.data || {};
          return { ok: d.success !== false, quote: d };
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[quoteMerchantWithdraw] failed:', MERCHANT_WITHDRAWS.quote, status, error.response && error.response.data);
          return {
            ok: false,
            status: status,
            // 404 means this build is pointed at a server without the quote endpoint.
            missing: isMissingEndpoint(error),
            message: extractError(error, 'Unable to price this withdrawal right now.')
          };
        })
      },

      /** POST merchant/withdraws - { bank, amount (CENTS), remarks } */
      createMerchantWithdraw({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        commit("setRegisterErrors", {});
        return axios
        .post(MERCHANT_WITHDRAWS.create, payload)
        .then(response => {
          const d = response.data || {};
          if (response.status === 200 || response.status === 201) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", d.message || 'Withdrawal request submitted.');
          }
          // Returned as well as committed: the screen shows the priced receipt
          // (fee, net, payout) straight from `withdraw` instead of re-deriving it.
          return { ok: true, message: d.message, withdraw: d.withdraw || {}, balance: d.balance };
        })
        .catch(error => {
          const parsed = parseRegisterError(error);
          console.warn('[createMerchantWithdraw] failed:', MERCHANT_WITHDRAWS.create, error.response && error.response.status, error.response && error.response.data);
          commit("setErrorMessage", 'errormessage');
          commit("setRegisterErrors", parsed.fieldErrors);
          commit("setMessage", parsed.message);
          return { ok: false, message: parsed.message, fieldErrors: parsed.fieldErrors };
        })
      },

      /** POST merchant/withdraws/{id}/cancel */
      cancelMerchantWithdraw({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        commit("setMessage", '');
        const id = payload && payload.id;
        const body = {};
        if (payload && payload.remarks) { body.remarks = payload.remarks; }
        return axios
        .post(MERCHANT_WITHDRAWS.cancel(id), body)
        .then(response => {
          if (response.status === 200 || response.status === 201) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", (response.data && response.data.message) || 'Withdrawal request cancelled.');
          }
        })
        .catch(error => {
          console.warn('[cancelMerchantWithdraw] failed:', MERCHANT_WITHDRAWS.cancel(id), error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to cancel this request.'));
        })
      },

      /**
       * POST merchant/withdraws/{id}/review - ADMIN action (approve / decline).
       * Not exposed on the merchant dashboard; ready for the admin screen.
       * payload = { id, status, remarks }
       */
      reviewMerchantWithdraw({commit}, payload){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        const id = payload && payload.id;
        return axios
        .post(MERCHANT_WITHDRAWS.review(id), { status: payload.status, remarks: payload.remarks || '' })
        .then(response => {
          if (response.status === 200 || response.status === 201) {
            commit("setSuccessMessage", 'success');
            commit("setMessage", (response.data && response.data.message) || 'Withdrawal reviewed.');
          }
        })
        .catch(error => {
          console.warn('[reviewMerchantWithdraw] failed:', MERCHANT_WITHDRAWS.review(id), error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to review this request.'));
        })
      },

      /** GET merchant/withdraws/analytics */
      getMerchantWithdrawAnalytics({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(MERCHANT_WITHDRAWS.analytics, { params: buildWithdrawQuery(filters) })
        .then(response => {
          commit('setMerchantWithdrawAnalytics', response.data || {});
        })
        .catch(error => {
          console.warn('[getMerchantWithdrawAnalytics] failed:', MERCHANT_WITHDRAWS.analytics, error.response && error.response.status);
          commit('setMerchantWithdrawAnalytics', {});
        })
      },

      /** GET merchant/dashboard - account, balance, payment totals, recent payments. */
      getMerchantDashboard({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(MERCHANT_INSIGHTS.dashboard, { params: buildInsightQuery(filters) })
        .then(response => {
          commit('setMerchantDashboard', response.data || {});
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[getMerchantDashboard] failed:', MERCHANT_INSIGHTS.dashboard, status);
          commit('setMerchantDashboard', {});
        })
      },

      /** GET merchant/analytics - totals, status breakdown and the daily series for the chart. */
      getMerchantAnalytics({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(MERCHANT_INSIGHTS.analytics, { params: buildInsightQuery(filters) })
        .then(response => {
          commit('setMerchantAnalytics', response.data || {});
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[getMerchantAnalytics] failed:', MERCHANT_INSIGHTS.analytics, status);
          commit('setMerchantAnalytics', {});
        })
      },

      /** GET merchant/customers - unique/repeat customers and the top customer list. */
      getMerchantCustomers({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        return axios
        .get(MERCHANT_INSIGHTS.customers, { params: buildInsightQuery(filters) })
        .then(response => {
          commit('setMerchantCustomers', response.data || {});
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[getMerchantCustomers] failed:', MERCHANT_INSIGHTS.customers, status);
          commit('setMerchantCustomers', {});
        })
      },

      /** Paginated merchant payment list. `filters` = {status, search, dateFrom, dateTo, limit, offset} */
      /**
       * Merchant wallet statement.
       * `silent` skips the store commit so the export can page through the same
       * endpoint without repainting the screen underneath the user.
       */
      /** GET statement - the signed-in user's wallet ledger. */
      listUserStatement({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setErrorMessage", '');
        return axios
        .get(USER_STATEMENT.list, { params: buildUserStatementQuery(filters) })
        .then(response => {
          const payload = normalizeStatement(response.data, filters);
          commit('setUserStatement', payload);
          return payload;
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[listUserStatement] failed:', USER_STATEMENT.list, status);
          commit('setUserStatement', normalizeStatement(null, filters));
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to load your statement.'));
          return null;
        })
      },

      /**
       * GET statement/{id} - one entry.
       * Carries fields the list omits: transaction_status, campaign_id, donor_id.
       */
      getUserStatementDetail({commit}, id){
        if (!id) { return Promise.resolve(null); }
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit('setUserStatementDetail', {});
        return axios
        .get(USER_STATEMENT.detail + id)
        .then(response => {
          const data = response.data || {};
          commit('setUserStatementDetail', data);
          return data;
        })
        .catch(error => {
          console.warn('[getUserStatementDetail] failed:', error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to load that entry.'));
          return null;
        })
      },

      /**
       * GET statement/export - the SERVER builds the file (?format=pdf|csv).
       *
       * Unlike the merchant statement, which had no export route and so was built in
       * the browser, this one is generated server-side. Paging and rebuilding it here
       * would produce a second, subtly different document.
       */
      exportUserStatement({commit}, filters){
        const f = Object.assign({}, filters || {});
        const format = (f.format || 'pdf').toLowerCase();
        delete f.limit;
        delete f.offset;
        f.format = format;

        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        return axios
        .get(USER_STATEMENT.export, {
          params: buildUserStatementQuery(f),
          responseType: 'blob'
        })
        .then(response => {
          const blob = response.data;
          const type = (blob && blob.type) || '';
          // Some backends answer a blob request with JSON - an error, or a file link.
          if (type.indexOf('application/json') !== -1) {
            return blob.text().then(function (text) {
              let parsed = {};
              try { parsed = JSON.parse(text); } catch (e) { parsed = {}; }
              const link = parsed.file_url || parsed.url || parsed.file;
              if (link) {
                window.open(link, '_blank');
                commit("setSuccessMessage", 'success');
                return { ok: true };
              }
              commit("setErrorMessage", 'errormessage');
              commit("setMessage", parsed.message || parsed.detail || 'Export failed.');
              return { ok: false };
            });
          }

          const disposition = (response.headers &&
            (response.headers['content-disposition'] || response.headers['Content-Disposition'])) || '';
          let filename = 'amejpay-statement.' + (format === 'csv' ? 'csv' : 'pdf');
          const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
          if (match && match[1]) { filename = decodeURIComponent(match[1].replace(/"/g, '').trim()); }

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          commit("setSuccessMessage", 'success');
          return { ok: true };
        })
        .catch(error => {
          console.warn('[exportUserStatement] failed:', error.response && error.response.status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to export the statement.'));
          return { ok: false };
        })
      },

      listMerchantStatement({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        const silent = Boolean(filters && filters.silent);
        if (!silent) { commit("setErrorMessage", ''); }
        return axios
        .get(MERCHANT_STATEMENT.list, { params: buildStatementQuery(filters) })
        .then(response => {
          const payload = normalizeStatement(response.data, filters);
          if (!silent) { commit('setMerchantStatement', payload); }
          return payload;
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[listMerchantStatement] failed:', MERCHANT_STATEMENT.list, status, error.response && error.response.data);
          if (!silent) {
            commit('setMerchantStatement', normalizeStatement(null, filters));
            commit("setErrorMessage", 'errormessage');
            commit("setMessage", extractError(error, 'Unable to load the statement.'));
          }
          return null;
        })
      },

      listMerchantPayments({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setErrorMessage", '');
        return axios
        .get(MERCHANT_PAYMENTS.list, { params: buildPaymentQuery(filters) })
        .then(response => {
          const d = response.data || {};
          const payload = Array.isArray(d)
            ? { count: d.length, limit: (filters && filters.limit) || d.length, offset: 0, results: d }
            : {
                count: d.count || 0,
                limit: d.limit || (filters && filters.limit) || 20,
                offset: d.offset || 0,
                results: Array.isArray(d.results) ? d.results : (Array.isArray(d.data) ? d.data : [])
              };
          commit('setMerchantPayments', payload);
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[listMerchantPayments] failed:', MERCHANT_PAYMENTS.list, status, error.response && error.response.data);
          commit('setMerchantPayments', { count: 0, limit: 20, offset: 0, results: [] });
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to load transactions.'));
        })
      },

      /** Single payment by pidx. */
      getMerchantPaymentByPidx({commit}, pidx){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit('setMerchantPaymentDetail', {});
        return axios
        .get(MERCHANT_PAYMENTS.detail + encodeURIComponent(pidx), {})
        .then(response => {
          const d = response.data || {};
          commit('setMerchantPaymentDetail', (d && (d.data || d.result)) ? (d.data || d.result) : d);
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[getMerchantPaymentByPidx] failed:', MERCHANT_PAYMENTS.detail + pidx, status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", extractError(error, 'Unable to load the transaction detail.'));
        })
      },

      /**
       * Export the current filter selection.
       * Requested as a blob so a CSV/XLSX body downloads directly; if the API instead
       * answers with JSON containing a link, that link is opened.
       */
      exportMerchantPayments({commit}, filters){
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + this.state.token;
        commit("setSuccessMessage", '');
        commit("setErrorMessage", '');
        return axios
        .get(MERCHANT_PAYMENTS.export, {
          params: buildPaymentQuery(filters),
          responseType: 'blob'
        })
        .then(response => {
          const blob = response.data;
          const type = (blob && blob.type) || '';
          // Some backends return JSON (an error, or {file_url}) with a blob responseType
          if (type.indexOf('application/json') !== -1) {
            return blob.text().then(function (text) {
              let parsed = {};
              try { parsed = JSON.parse(text); } catch (e) { parsed = {}; }
              const link = parsed.file_url || parsed.url || parsed.file;
              if (link) {
                window.open(link, '_blank');
                commit("setSuccessMessage", 'success');
              } else {
                commit("setErrorMessage", 'errormessage');
                commit("setMessage", parsed.message || parsed.detail || 'Export failed.');
              }
            });
          }
          const disposition = (response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition'])) || '';
          let filename = 'merchant-transactions.csv';
          const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
          if (match && match[1]) { filename = decodeURIComponent(match[1].replace(/"/g, '').trim()); }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.setAttribute('download', filename);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(function () { window.URL.revokeObjectURL(url); }, 1000);
          commit("setSuccessMessage", 'success');
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.warn('[exportMerchantPayments] failed:', MERCHANT_PAYMENTS.export, status);
          commit("setErrorMessage", 'errormessage');
          commit("setMessage", 'Unable to export transactions.');
        })
      },

      listSuccessTransactions({ commit}, pageId){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('successtransactions?page='+pageId, {})
        .then(response => {
          commit('setTransactions',response.data)
        })
      },
      
      listFailedTransactions({ commit}, pageId){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('failedtransactions?page='+pageId, {})
        .then(response => {
          commit('setTransactions',response.data)
        })
      },

      getMerchantWithdraws({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('merchantwithdraw', {})
        .then(response => {
              commit('setMerchantWithdraw',response.data)
          })
      },
      
      postMerchantWithdraw({commit}, formData){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .post('merchantwithdraw', formData)
        .then(response => {
          if(response.status == 200){
            commit("setSuccessMessage",'success')
          }
        })
        .catch(error=>{
          if (error){
            commit("setErrorMessage", 'errormessage')
            commit("setMessage", error.response.data.message)
          }
        })
      },

      getMerchantKeys({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('merchant/keys', {})
        .then(response => {
              commit('setMerchantKey',response.data)
          })
      },
      
      getMerchantDashboardCount({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('dashboardcount', {})
        .then(response => {
              commit('setMerchantDashboardCount',response.data)
          })
        .catch(error=>{
          // ✅ FIXED: Added error handling for 403/404 endpoints
          console.warn('Dashboard Count API error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.message
          });
          // Don't crash - endpoint might be removed or user might lack permission
          commit('setMerchantDashboardCount', {})
          // Don't re-throw - let page show empty data
        })
      },
      
      getMerchantDashboardTransaction({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('dashboardtransaction', {})
        .then(response => {
              commit('setMerchantDashboardTransaction',response.data)
          })
        .catch(error=>{
          // ✅ FIXED: Added error handling for 403/404 endpoints
          console.warn('Dashboard Transaction API error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.message
          });
          // Don't crash - endpoint might be removed or user might lack permission
          commit('setMerchantDashboardTransaction', {})
          // Don't re-throw - let page show empty data
        })
      },
      
      getMerchantDashboardCashFlow({commit}){
        axios.defaults.headers.common['Authorization']  = 'Bearer ' + this.state.token
        return axios
        .get('dashboardcashflow', {})
        .then(response => {
              commit('setMerchantDashboardCashFlow',response.data)
          })
        .catch(error=>{
          // ✅ FIXED: Added error handling for 403/404 endpoints
          console.warn('Dashboard CashFlow API error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.message
          });
          // Don't crash - endpoint might be removed or user might lack permission
          commit('setMerchantDashboardCashFlow', {})
          // Don't re-throw - let page show empty data
        })
      },

    
}
